import React, { useState, useEffect, useMemo } from "react";
import { isImpersonating, guardAction } from "../../utils/impersonate";
import usePageTitle from '../../hooks/usePageTitle';
import { usePartner } from '../../context/PartnerContext';
import { fMoney, fPct, fRatio, fCount, fPrice } from "../../utils/format";
import { useSearchParams } from "react-router-dom";
import api from "../../api/api";
import AddAccountModal from "../../components/modals/AddAccountModal";
import BecomeAnalystCard from "./BecomeAnalystCard";

const gradeColor = g =>
  g?.startsWith("A") ? "var(--success)" :
  g?.startsWith("B") ? "var(--accent)" :
  g?.startsWith("C") ? "var(--warning)" : "var(--danger)";

const riskColor = r =>
  r==="EXTREME" ? "var(--danger)" : r==="HIGH" ? "#fb923c" :
  r==="VERIFIED_SAFE"||r==="LOW" ? "var(--success)" : "var(--warning)";

const riskBg = r =>
  r==="EXTREME" ? "var(--danger-soft)" : r==="HIGH" ? "rgba(251,146,60,0.12)" :
  r==="VERIFIED_SAFE"||r==="LOW" ? "var(--success-soft)" : "var(--warning-soft)";

const sevColor = s =>
  s==="CRITICAL" ? "var(--danger)" : s==="MAJOR" ? "var(--warning)" : "var(--purple,#a78bfa)";
const sevBg = s =>
  s==="CRITICAL" ? "var(--danger-soft)" : s==="MAJOR" ? "var(--warning-soft)" : "rgba(167,139,250,0.12)";

const card = {
  background: "var(--bg-surface)",
  border: "1px solid var(--border)",
  borderRadius: "var(--radius-xl)",
  padding: 20,
  marginBottom: 16,
};

const th = {
  padding: "8px 12px", textAlign: "left", fontSize: 11, fontWeight: 600,
  color: "var(--text-faint)", borderBottom: "1px solid var(--border-subtle)",
  whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: "0.05em",
};
const td = {
  padding: "10px 12px", fontSize: 13, color: "var(--text-muted)",
  borderBottom: "1px solid var(--border-subtle)",
};


// ─── Format Helpers (NUMBER_FORMAT.md — 2 decimal, K/M >= 1000) ─────────────
const fmt2 = v => { const n=Number(v)||0; if(Math.abs(n)>=1000000)return(n/1000000).toFixed(2)+"M"; if(Math.abs(n)>=1000)return(n/1000).toFixed(2)+"K"; return n.toFixed(2); };
const fmtPct = v => fmt2(Number(v)||0)+"%";
const fmtDollar = v => { const n=Number(v)||0; return(n>=0?"$":"-$")+fmt2(Math.abs(n)); };
const fmtDollarSign = v => { const n=Number(v)||0; return(n>=0?"+$":"-$")+fmt2(Math.abs(n)); };
// ─────────────────────────────────────────────────────────────────────────────


function StatCard({ label, value, sub, color, subColor }) {
  return (
    <div style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)",
      borderRadius: "var(--radius-lg)", padding: "14px 18px" }}>
      <div style={{ fontSize: 10, color: "var(--text-faint)", fontWeight: 600,
        letterSpacing: "0.07em", textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: String(value).length > 12 ? 13 : String(value).length > 9 ? 15 : String(value).length > 7 ? 18 : 22, fontWeight: 700, color: color || "var(--text-number)", lineHeight: 1.1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: subColor || "var(--text-faint)", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function PillarBar({ name, score, reason }) {
  const color = "var(--accent)";
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500 }}>{name}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color }}>{fmt2(score)}</span>
      </div>
      <div style={{ height: 5, background: "var(--bg-elevated)", borderRadius: "var(--radius-full)" }}>
        <div style={{ height: 5, borderRadius: "var(--radius-full)", background: color,
          width: `${Math.min(score, 100)}%`, transition: "width 0.6s ease" }} />
      </div>
      {reason && <div style={{ fontSize: 10, color: "var(--text-faint)", marginTop: 2 }}>{reason}</div>}
    </div>
  );
}

function GradeCircle({ score, grade }) {
  const color = gradeColor(grade);
  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ width: 80, height: 80, borderRadius: "50%",
        border: `4px solid ${color}`, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center", margin: "0 auto 8px" }}>
        <div style={{ fontSize: 22, fontWeight: 800, color }}>{grade}</div>
      </div>
      <div style={{ fontSize: 18, fontWeight: 700, color }}>{fmt2(score)}</div>
      <div style={{ fontSize: 11, color: "var(--text-faint)" }}>AlphaScore</div>
    </div>
  );
}

function MonthlyChart({ accountId, accountCreatedAt, deposit, mode }) {
  const [data, setData] = useState([]);
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());

  useEffect(() => {
    if (!accountId) return;
    const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
    const endpoint = mode === "PLAN" ? "plan-monthly-performance" : "monthly-performance";
    fetch(`/api/trader/${endpoint}?account_id=${accountId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()).then(d => { setData(d.months || []); });
  }, [accountId, mode]);

  const dataYears = [...new Set(data.map(m => m.year))];
  const createdYear = accountCreatedAt ? new Date(accountCreatedAt).getFullYear() : now.getFullYear();
  const startYear = dataYears.length > 0 ? Math.min(...dataYears, createdYear) : createdYear;
  const years = [];
  for (let y = now.getFullYear(); y >= startYear; y--) years.push(y);

  const MONTH_NAMES = ["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const dataMap = {};
  data.forEach(m => { dataMap[`${m.year}-${m.month}`] = m; });

  const filtered = [1,2,3,4,5,6,7,8,9,10,11,12].map(mIdx => {
    const actual = dataMap[`${year}-${MONTH_NAMES[mIdx]}`];
    return { month: MONTH_NAMES[mIdx], profit: actual?.profit || 0,
      trades: actual?.trades || 0, hasData: !!actual };
  });

  const maxProfit = Math.max(...filtered.map(m => Math.abs(m.profit)), 1);
  const selStyle = { background: "var(--bg-elevated)", border: "1px solid var(--border)",
    color: "var(--text-main)", borderRadius: "var(--radius-md)", padding: "5px 10px", fontSize: 12 };

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-main)" }}>Monthly Performance{mode==="PLAN"?" (Plan)":""}</div>
        <select value={year} onChange={e => setYear(Number(e.target.value))} style={selStyle}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <div style={{ display: "flex", gap: 6, alignItems: "flex-end", height: 120, overflowX: "auto" }}>
        {filtered.map(m => {
          const pct = m.profit === 0 ? 0 : Math.max((Math.abs(m.profit) / maxProfit) * 100, 8);
          const positive = m.profit >= 0;
          const cumulative = filtered.slice(0, filtered.indexOf(m) + 1).reduce((s, x) => s + x.profit, 0);
          const roiPct = deposit > 0 ? (cumulative / deposit * 100) : 0;
          const profitLabel = m.profit === 0 ? "—" : ((positive ? "+$" : "-$") +
            fmt2(Math.abs(m.profit)));
          return (
            <div key={m.month} style={{ flex: 1, minWidth: 36, display: "flex",
              flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
              <div style={{ fontSize: 10, fontWeight: 600,
                color: m.profit === 0 ? "var(--text-faint)" : positive ? "var(--success)" : "var(--danger)" }}>
                {profitLabel}
              </div>
              {m.profit !== 0 && deposit > 0 && (
                <div style={{ fontSize: 9, color: "var(--text-faint)", fontWeight: 600 }}>
                  {roiPct >= 0 ? "+" : ""}{fmtPct(Math.abs(roiPct))}
                </div>
              )}
              <div style={{ width: "100%", height: `${pct}%`, borderRadius: "4px 4px 0 0",
                background: m.profit === 0 ? "var(--bg-elevated)" : positive ? "var(--success)" : "var(--danger)",
                opacity: m.profit === 0 ? 0.4 : 0.85, minHeight: m.profit === 0 ? 3 : 8 }} />
              <div style={{ fontSize: 10, color: "var(--text-faint)" }}>{m.month}</div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12, fontSize: 11, color: "var(--text-faint)" }}>
        <span>Months with data: {filtered.filter(m => m.hasData).length}</span>
        <span>Profitable: {filtered.filter(m => m.profit > 0).length}</span>
        <span>Loss: {filtered.filter(m => m.profit < 0).length}</span>
      </div>
    </div>
  );
}

function getWeeksInMonth(month, year) {
  const weeks = [];
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  let cur = new Date(firstDay);
  const dow = cur.getDay() || 7;
  cur.setDate(cur.getDate() - dow + 1);
  while (cur <= lastDay) {
    const jan4 = new Date(cur.getFullYear(), 0, 4);
    const startW1 = new Date(jan4);
    startW1.setDate(jan4.getDate() - (jan4.getDay() || 7) + 1);
    const weekNum = Math.round((cur - startW1) / (7 * 86400000)) + 1;
    const weekYear = cur >= new Date(cur.getFullYear(), 11, 29) ? cur.getFullYear() + 1
      : cur < new Date(cur.getFullYear(), 0, 4) ? cur.getFullYear() - 1 : cur.getFullYear();
    if (cur <= lastDay) weeks.push({ weekNum, weekYear, monday: new Date(cur) });
    cur.setDate(cur.getDate() + 7);
  }
  return weeks;
}

function getISOWeekMonday(week, year) {
  const jan4 = new Date(year, 0, 4);
  const startOfWeek1 = new Date(jan4);
  startOfWeek1.setDate(jan4.getDate() - (jan4.getDay() || 7) + 1);
  const monday = new Date(startOfWeek1);
  monday.setDate(startOfWeek1.getDate() + (week - 1) * 7);
  return monday;
}

function WeeklyChart({ accountId, accountCreatedAt, deposit, mode }) {
  const [data, setData] = React.useState([]);
  const now = new Date();
  const [selYear, setSelYear] = useState(now.getFullYear());
  const [selMonth, setSelMonth] = useState(now.getMonth() + 1);

  React.useEffect(() => {
    if (!accountId) return;
    const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
    const endpoint = mode === "PLAN" ? "plan-weekly-performance" : "weekly-performance";
    fetch(`/api/trader/${endpoint}?account_id=${accountId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { setData(d.weeks || []); });
  }, [accountId, mode]);

  const MONTH_NAMES = ["","Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  const dataYears = [...new Set(data.map(w => getISOWeekMonday(w.week, w.year).getFullYear()))];
  const createdYear = accountCreatedAt ? new Date(accountCreatedAt).getFullYear() : now.getFullYear();
  const startYear = dataYears.length > 0 ? Math.min(...dataYears, createdYear) : createdYear;
  const years = [];
  for (let y = now.getFullYear(); y >= startYear; y--) years.push(y);

  const weeksInMonth = getWeeksInMonth(selMonth, selYear);
  const dataMap = {};
  data.forEach(w => { dataMap[`${w.year}-${w.week}`] = w; });

  const filtered = weeksInMonth.map(({ weekNum, weekYear, monday }) => {
    const actual = dataMap[`${weekYear}-${weekNum}`];
    return { week: weekNum, year: weekYear, monday, profit: actual?.profit || 0,
      trades: actual?.trades || 0, hasData: !!actual };
  });

  const maxP = Math.max(...filtered.map(w => Math.abs(w.profit)), 1);
  const selStyle = { background: "var(--bg-elevated)", border: "1px solid var(--border)",
    color: "var(--text-main)", borderRadius: "var(--radius-md)", padding: "5px 10px", fontSize: 12 };

  return (
    <div style={card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-main)" }}>Weekly Performance{mode==="PLAN"?" (Plan)":""}</div>
        <div style={{ display: "flex", gap: 6 }}>
          <select value={selMonth} onChange={e => setSelMonth(Number(e.target.value))} style={selStyle}>
            {[1,2,3,4,5,6,7,8,9,10,11,12].map(m => <option key={m} value={m}>{MONTH_NAMES[m]}</option>)}
          </select>
          <select value={selYear} onChange={e => setSelYear(Number(e.target.value))} style={selStyle}>
            {years.map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>
      <div style={{ display: "flex", gap: 4, alignItems: "flex-end", height: 100, overflowX: "auto" }}>
        {filtered.map((w, i) => {
          const pct = w.profit === 0 ? 0 : Math.max((Math.abs(w.profit) / maxP) * 90, 8);
          const pos = w.profit >= 0;
          const dateLabel = w.monday.getDate() + "/" + (w.monday.getMonth() + 1);
          const cumulative = filtered.slice(0, i + 1).reduce((s, x) => s + x.profit, 0);
          const roiPct = deposit > 0 ? (cumulative / deposit * 100) : 0;
          const profitLabel = w.profit === 0 ? "—" : ((pos ? "+$" : "-$") +
            fmt2(Math.abs(w.profit)));
          return (
            <div key={i} style={{ flex: 1, minWidth: 44, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
              <div style={{ fontSize: 9, fontWeight: 600,
                color: w.profit === 0 ? "var(--text-faint)" : pos ? "var(--success)" : "var(--danger)" }}>
                {profitLabel}
              </div>
              {w.profit !== 0 && deposit > 0 && (
                <div style={{ fontSize: 8, color: "var(--text-faint)", fontWeight: 600 }}>
                  {roiPct >= 0 ? "+" : ""}{fmtPct(Math.abs(roiPct))}
                </div>
              )}
              <div style={{ width: "100%", height: `${pct}%`, borderRadius: "3px 3px 0 0",
                background: w.profit === 0 ? "var(--bg-elevated)" : pos ? "var(--success)" : "var(--danger)",
                opacity: w.profit === 0 ? 0.4 : 0.85, minHeight: w.profit === 0 ? 3 : 8 }} />
              <div style={{ fontSize: 9, color: "var(--text-faint)" }}>W{w.week}</div>
              <div style={{ fontSize: 8, color: "var(--text-faint)", opacity: 0.6 }}>{dateLabel}</div>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, fontSize: 11, color: "var(--text-faint)" }}>
        <span>Weeks: {filtered.filter(w => w.hasData).length}</span>
        <span>Profitable: {filtered.filter(w => w.profit > 0).length}</span>
        <span>Trades: {filtered.reduce((s, w) => s + w.trades, 0)}</span>
      </div>
    </div>
  );
}

function PerPairTable({ perPairData }) {
  if (!perPairData?.pairs?.length) return (
    <div style={{ ...card, textAlign: "center", color: "var(--text-faint)", padding: 40 }}>No per-pair data available (min. 20 closed trades per pair)</div>
  );
  return (
    <div style={card}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 14 }}>Performance Per Trading Pair</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>{["Pair","Trades","Win%","Avg RR","Net P&L","Flags"].map(h => (
              <th key={h} style={th}>{h}</th>
            ))}</tr>
          </thead>
          <tbody>
            {perPairData.pairs.filter(p => (p.trades || 0) >= 20).map(p => (
              <tr key={p.symbol}>
                <td style={{ ...td, fontWeight: 700 }}>{p.symbol || "—"}</td>
                <td style={td}>{p.trades || 0}</td>
                <td style={td}>{fPct(p.win_rate ?? p.winRate)}</td>
                <td style={td}>{fRatio(p.avg_rr ?? p.avgRR)}</td>
                <td style={{ ...td, color: (p.net_pnl ?? p.netPnl ?? 0) >= 0 ? "var(--success)" : "var(--danger)", fontWeight: 600 }}>
                  {fMoney(p.net_pnl ?? p.netPnl, { sign: true })}
                </td>
                <td style={td}>
                  {p.flags?.length > 0 ? p.flags.map((f, i) => (
                    <span key={i} style={{ fontSize: 10, padding: "2px 6px", borderRadius: "var(--radius-sm)",
                      marginRight: 4, background: sevBg(f.severity), color: sevColor(f.severity) }}>
                      {f.softTitle || f.title || f.name}
                    </span>
                  )) : <span style={{ color: "var(--text-faint)" }}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


function LivePositions({ accountId }) {
  const [trades, setTrades] = React.useState([]);
  const [loading, setLoading] = React.useState(true);

  React.useEffect(() => {
    if (!accountId) return;
    const load = () => {
      fetch(`/api/trader/open-trades?account_id=${accountId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      })
        .then(r => r.json())
        .then(d => { setTrades(d.trades || []); setLoading(false); });
    };
    load();
    const interval = setInterval(load, 5000);
    return () => clearInterval(interval);
  }, [accountId]);

  if (loading) return <div style={{ padding: 24, color: "var(--text-muted)" }}>Loading...</div>;
  if (trades.length === 0) return <div style={{ padding: 24, color: "var(--text-muted)" }}>No open positions</div>;

  return (
    <div style={{ padding: "16px 0" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr style={{ borderBottom: "1px solid var(--border)" }}>
            {["Ticket","Symbol","Type","Lots","Open Price","Profit","Open Time"].map(h => (
              <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontSize: 11, color: "var(--text-muted)", fontWeight: 600 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {trades.map(t => (
            <tr key={t.ticket} style={{ borderBottom: "1px solid var(--border)" }}>
              <td style={{ padding: "8px 12px", fontSize: 12 }}>{t.ticket}</td>
              <td style={{ padding: "8px 12px", fontSize: 12, fontWeight: 600 }}>{t.symbol}</td>
              <td style={{ padding: "8px 12px", fontSize: 12, color: t.type === "buy" ? "var(--success)" : "var(--danger)", textTransform: "uppercase" }}>{t.type}</td>
              <td style={{ padding: "8px 12px", fontSize: 12 }}>{t.lots}</td>
              <td style={{ padding: "8px 12px", fontSize: 12 }}>{t.openPrice}</td>
              <td style={{ padding: "8px 12px", fontSize: 12, color: t.profit >= 0 ? "var(--success)" : "var(--danger)", fontWeight: 600 }}>{t.profit >= 0 ? "+" : ""}{t.profit.toFixed(2)}</td>
              <td style={{ padding: "8px 12px", fontSize: 12, color: "var(--text-muted)" }}>{t.openTime ? t.openTime.substring(0, 16) : "-"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TradeHistory({ accountId, mode, setMode, planData }) {
  const [liveTrades, setLive]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [limit, setLimit]       = useState(20);
  const [offset, setOffset]     = useState(0);
  // Toggle LIVE/PLAN sekarang global (baris tab), reset pagination saat mode berubah
  useEffect(() => { setOffset(0); }, [mode]);
  const [planOffset, setPlanOffset] = useState(0);
  const [totalLive, setTotalLive] = useState(0);

  const token = () => localStorage.getItem("access_token") || localStorage.getItem("token") || "";

  // Fetch LIVE trades
  useEffect(() => {
    if (!accountId) return;
    setLoading(true);
    fetch(`/api/trader/trades?account_id=${accountId}&limit=${limit}&offset=${offset}`, {
      headers: { Authorization: `Bearer ${token()}` }
    }).then(r => r.json()).then(d => {
      setLive(d.trades || []); setTotalLive(d.total || 0);
    }).finally(() => setLoading(false));
  }, [accountId, limit, offset]);


  const selStyle = { background: "var(--bg-elevated)", border: "1px solid var(--border)",
    color: "var(--text-main)", borderRadius: "var(--radius-md)", padding: "5px 10px", fontSize: 12 };
  const btnStyle = (disabled) => ({
    padding: "5px 12px", borderRadius: "var(--radius-md)",
    border: "1px solid var(--border)", fontSize: 12, cursor: disabled ? "not-allowed" : "pointer",
    background: disabled ? "var(--bg-subtle)" : "var(--bg-elevated)",
    color: disabled ? "var(--text-faint)" : "var(--text-main)",
  });

  const isLive   = mode === "LIVE";
  const ps       = planData?.plan_summary || {};
  // Filter hanya trades dengan SL+TP, sort newest first (fix Go time string)
  const parseCloseTime = s => s ? s.slice(0,19) : '';
  const withSlTp = (planData?.trades || []).filter(t => t.planStatus === 'RESOLVED');
  const allPlanTrades = [...withSlTp].sort((a,b) => parseCloseTime(b.closeTime).localeCompare(parseCloseTime(a.closeTime)));
  const planTradesAsc = [...withSlTp].sort((a,b) => parseCloseTime(a.closeTime).localeCompare(parseCloseTime(b.closeTime)));
  const planCurveTrades = planTradesAsc.filter(t => t.planStatus === 'RESOLVED');
  const planTrades = allPlanTrades.slice(planOffset, planOffset + limit);
  const totalPlan  = allPlanTrades.length;
  const mcIndex  = planCurveTrades.findIndex(t => t.drawdownPct >= 100);

  // Plan Curve — DD Underwater + Equity Growth (dropdown)
  // Equity boleh negatif, peak monotonic, DD natural recovery
  const PlanCurve = () => {
    const [hov, setHov]             = React.useState(null);
    const [curveMode, setCurveMode] = React.useState('dd');
    const svgRef = React.useRef(null);
    if (planCurveTrades.length === 0) return null;

    const W=900, H=180, PAD={top:16,right:16,bottom:32,left:60};
    const iW=W-PAD.left-PAD.right, iH=H-PAD.top-PAD.bottom;

    // Stats
    const startEq   = (planCurveTrades[0].runningEquity||0)-(planCurveTrades[0].planPnl||0);
    const currentEq = planCurveTrades[planCurveTrades.length-1].runningEquity||0;
    const peakEq    = Math.max(...planCurveTrades.map(t=>t.runningEquity||0), startEq);
    const planMaxDD = Math.max(...planCurveTrades.map(t=>t.drawdownPct||0));
    const ddColor   = planMaxDD>=100?"var(--danger)":planMaxDD>40?"var(--danger)":planMaxDD>20?"var(--warning)":"var(--success)";
    const fmtDate   = str => { if(!str)return""; const d=new Date(str.slice(0,19).replace(' ','T')); return `${d.getDate()}/${d.getMonth()+1}`; };
    const fmtMoney  = v => {
      const sign = v<0?"-":"";
      const abs  = Math.abs(v);
      return sign+(abs>=1000?`$${(abs/1000).toFixed(2)}K`:`$${abs.toFixed(2)}`);
    };

    // X axis: index-based (trade sequence)
    const n   = planCurveTrades.length;
    const xOf = i => PAD.left+(i/Math.max(n-1,1))*iW;
    const xTickIdxs = n<=1?[0]:Array.from({length:Math.min(6,n)},(_,i)=>Math.round(i*(n-1)/(Math.min(6,n)-1)));

    // DD y-scale (0% top, minDD bottom)
    const minDD   = Math.min(...planCurveTrades.map(t=>-(t.drawdownPct||0)),-5)*1.05;
    const yDD     = v => PAD.top+(v/minDD)*iH;
    const yTicksDD= [0,-10,-20,-30,-50,-75,-100].filter(v=>v>=minDD*0.95);
    const ddPath  = planCurveTrades.map((t,i)=>`${i===0?"M":"L"}${xOf(i).toFixed(1)},${yDD(-(t.drawdownPct||0)).toFixed(1)}`).join(" ");
    const ddFill  = ddPath+` L${xOf(n-1).toFixed(1)},${PAD.top} L${xOf(0).toFixed(1)},${PAD.top} Z`;

    // Equity y-scale — handle negative
    const minEq  = Math.min(...planCurveTrades.map(t=>t.runningEquity||0), startEq, 0);
    const maxEq  = Math.max(peakEq, 0.01);
    const eqRange= maxEq - minEq;
    const yEq    = v => PAD.top + (maxEq - v) / eqRange * iH;
    const yZero  = yEq(0); // posisi garis nol
    const eqPts  = [startEq, ...planCurveTrades.map(t=>t.runningEquity||0)];
    const eqPath = eqPts.map((v,i)=>`${i===0?"M":"L"}${xOf(i===0?0:i-1).toFixed(1)},${yEq(v).toFixed(1)}`).join(" ");
    // Fill: area positif (biru), area negatif (merah)
    const eqFillPos = eqPath+` L${xOf(n-1).toFixed(1)},${yZero} L${xOf(0).toFixed(1)},${yZero} Z`;

    // First blow-up point (equity first <= 0)
    const blownIdx = planCurveTrades.findIndex(t=>(t.runningEquity||0)<=0);

    const handleMouse = e => {
      const rect=svgRef.current?.getBoundingClientRect(); if(!rect)return;
      const x=(e.clientX-rect.left)/rect.width*W;
      const idx=Math.min(n-1,Math.max(0,Math.round((x-PAD.left)/iW*(n-1))));
      setHov({idx,x:xOf(idx)});
    };
    const selStyle={background:"var(--bg-base)",border:"1px solid var(--border)",color:"var(--text-main)",
      borderRadius:"var(--radius-md)",padding:"4px 8px",fontSize:11,cursor:"pointer"};

    return (
      <div style={{marginBottom:14,background:"var(--bg-elevated)",borderRadius:"var(--radius-md)",padding:"12px 8px 10px"}}>
        {/* Header */}
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10,paddingLeft:4}}>
          <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
            <span style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>
              {curveMode==="dd"?"Plan Drawdown — Underwater Curve":"Plan Equity Growth Curve"}
            </span>
            <span style={{fontSize:12,fontWeight:700,padding:"2px 8px",borderRadius:"var(--radius-sm)",
              background:"rgba(239,68,68,0.1)",color:ddColor}}>Max DD: {planMaxDD.toFixed(2)}%</span>
            {blownIdx>=0&&<span style={{fontSize:11,fontWeight:600,color:"var(--danger)",
              background:"rgba(239,68,68,0.08)",padding:"2px 8px",borderRadius:"var(--radius-sm)"}}>
                 Blown #{blownIdx+1}
            </span>}
          </div>
          <select value={curveMode} onChange={e=>{setCurveMode(e.target.value);setHov(null);}} style={selStyle}>
            <option value="dd">DD Curve</option>
            <option value="equity">Equity Curve</option>
          </select>
        </div>

        <div style={{position:"relative"}}>
          <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:"auto",display:"block"}}
            onMouseMove={handleMouse} onMouseLeave={()=>setHov(null)}>
            <defs>
              <linearGradient id="pdd-fill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(239,68,68,0.35)"/>
                <stop offset="100%" stopColor="rgba(239,68,68,0.05)"/>
              </linearGradient>
              <linearGradient id="peq-pos" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="rgba(78,154,241,0.28)"/>
                <stop offset="100%" stopColor="rgba(78,154,241,0.04)"/>
              </linearGradient>
            </defs>

            {curveMode==="dd"?(
              <>
                {yTicksDD.map(v=>(
                  <g key={v}>
                    <line x1={PAD.left} y1={yDD(v)} x2={W-PAD.right} y2={yDD(v)}
                      stroke="var(--border-subtle)" strokeWidth="0.5" strokeDasharray="4 4"/>
                    <text x={PAD.left-4} y={yDD(v)+4} textAnchor="end" fontSize="10" fill="var(--text-faint)">{v}%</text>
                  </g>
                ))}
                <line x1={PAD.left} y1={PAD.top} x2={W-PAD.right} y2={PAD.top} stroke="var(--border)" strokeWidth="1"/>
                <path d={ddFill} fill="url(#pdd-fill)"/>
                <path d={ddPath} fill="none" stroke="var(--danger)" strokeWidth="1.5"/>
                {blownIdx>=0&&(
                  <g>
                    <line x1={xOf(blownIdx)} y1={PAD.top} x2={xOf(blownIdx)} y2={PAD.top+iH}
                      stroke="rgba(239,68,68,0.5)" strokeWidth="1.5" strokeDasharray="4 3"/>
                    <text x={xOf(blownIdx)+3} y={PAD.top+10} fontSize="9" fill="rgba(239,68,68,0.9)" fontWeight="700">Blown</text>
                  </g>
                )}
                {xTickIdxs.map(i=><text key={i} x={xOf(i)} y={H-6} textAnchor="middle" fontSize="10" fill="var(--text-faint)">
                  {fmtDate(planCurveTrades[i]?.closeTime)}
                </text>)}
                {hov&&<>
                  <line x1={hov.x} y1={PAD.top} x2={hov.x} y2={PAD.top+iH} stroke="var(--danger)" strokeWidth="0.8" opacity="0.4"/>
                  <circle cx={hov.x} cy={yDD(-(planCurveTrades[hov.idx]?.drawdownPct||0))} r="4"
                    fill="var(--danger)" stroke="var(--bg-surface)" strokeWidth="2"/>
                </>}
              </>
            ):(
              <>
                {/* Y grid */}
                {[minEq,minEq/2,0,maxEq*0.5,maxEq].map((v,i)=>(
                  <g key={i}>
                    <line x1={PAD.left} y1={yEq(v)} x2={W-PAD.right} y2={yEq(v)}
                      stroke={v===0?"var(--border)":"var(--border-subtle)"}
                      strokeWidth={v===0?1:0.5} strokeDasharray={v===0?"":"4 4"}/>
                    <text x={PAD.left-4} y={yEq(v)+4} textAnchor="end" fontSize="10" fill="var(--text-faint)">{fmtMoney(v)}</text>
                  </g>
                ))}
                {/* Positive fill */}
                <path d={eqFillPos} fill="url(#peq-pos)"/>
                {/* Negative zone fill */}
                {minEq<0&&<rect x={PAD.left} y={yZero} width={iW} height={yEq(minEq)-yZero}
                  fill="rgba(239,68,68,0.06)"/>}
                {/* Equity line */}
                <path d={eqPath} fill="none" stroke="#4e9af1" strokeWidth="1.5"/>
                {/* Zero line label */}
                {minEq<0&&<text x={PAD.left-4} y={yZero+4} textAnchor="end" fontSize="10" fill="var(--text-faint)">$0</text>}
                {blownIdx>=0&&(
                  <g>
                    <line x1={xOf(blownIdx)} y1={PAD.top} x2={xOf(blownIdx)} y2={PAD.top+iH}
                      stroke="rgba(239,68,68,0.5)" strokeWidth="1.5" strokeDasharray="4 3"/>
                    <text x={xOf(blownIdx)+3} y={PAD.top+10} fontSize="9" fill="rgba(239,68,68,0.9)" fontWeight="700">Blown</text>
                  </g>
                )}
                {xTickIdxs.map(i=><text key={i} x={xOf(i===0?0:i)} y={H-6} textAnchor="middle" fontSize="10" fill="var(--text-faint)">
                  {i===0?"Start":fmtDate(planCurveTrades[i-1]?.closeTime)}
                </text>)}
                {hov&&<>
                  <line x1={hov.x} y1={PAD.top} x2={hov.x} y2={PAD.top+iH} stroke="#4e9af1" strokeWidth="0.8" opacity="0.4"/>
                  <circle cx={hov.x} cy={yEq(planCurveTrades[hov.idx]?.runningEquity||0)} r="4"
                    fill="#4e9af1" stroke="var(--bg-surface)" strokeWidth="2"/>
                </>}
              </>
            )}
          </svg>

          {/* Tooltip */}
          {hov&&hov.idx!=null&&planCurveTrades[hov.idx]&&(()=>{
            const t=planCurveTrades[hov.idx];
            return (
              <div style={{position:"absolute",top:8,
                left:hov.idx>n*0.6?"auto":(hov.x/W*100)+"%",
                right:hov.idx>n*0.6?(100-hov.x/W*100)+"%":"auto",
                background:"var(--bg-elevated)",border:"1px solid var(--border)",
                borderRadius:"var(--radius-md)",padding:"8px 12px",fontSize:11,
                pointerEvents:"none",whiteSpace:"nowrap",zIndex:10}}>
                <div style={{color:"var(--text-faint)",marginBottom:4}}>{fmtDate(t.closeTime)}</div>
                {curveMode==="dd"
                  ?<div style={{fontWeight:700,color:"var(--danger)"}}>DD: {(t.drawdownPct||0).toFixed(2)}%</div>
                  :<div style={{fontWeight:700,color:"#4e9af1"}}>Equity: {fmtMoney(t.runningEquity||0)}</div>}
                <div style={{color:"var(--text-muted)",marginTop:2}}>
                  {t.symbol} {(t.type||"").toUpperCase()} — {t.planResult==="TP"
                    ?<span style={{color:"var(--success)"}}>TP </span>
                    :<span style={{color:"var(--danger)"}}>SL </span>}
                </div>
              </div>
            );
          })()}
        </div>

        {/* Stats bar */}
        <div style={{display:"flex",gap:20,marginTop:10,paddingLeft:PAD.left,fontSize:11,flexWrap:"wrap"}}>
          {[
            {label:"START",   val:fmtMoney(startEq),   color:"var(--text-main)"},
            {label:"CURRENT", val:fmtMoney(currentEq),  color:currentEq>=startEq?"var(--success)":"var(--danger)"},
            {label:"PEAK",    val:fmtMoney(peakEq),     color:"var(--text-main)"},
            {label:"MAX DD",  val:`${planMaxDD.toFixed(2)}%`, color:ddColor},
          ].map(({label,val,color})=>(
            <div key={label}>
              <span style={{color:"var(--text-faint)",marginRight:4,fontSize:9,fontWeight:600}}>{label}</span>
              <span style={{fontWeight:700,color}}>{val}</span>
            </div>
          ))}
        </div>

        {/* Blown detail */}
        {blownIdx>=0&&(()=>{
          const bt=planCurveTrades[blownIdx];
          return <div style={{fontSize:11,color:"var(--danger)",marginTop:6,paddingLeft:4}}>
               {bt.symbol} {(bt.type||"").toUpperCase()} — Entry: {bt.openPrice} | SL: {bt.sl} | TP: {bt.tp||"—"}
          </div>;
        })()}
      </div>
    );
  };

  return (
    <div style={card}>
      {/* Header */}
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,flexWrap:"wrap",gap:8}}>
        <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>Trade History</div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          {isLive && (
            <select value={limit} onChange={e=>{setLimit(Number(e.target.value));setOffset(0);}} style={selStyle}>
              {[20,50,100,200].map(n=><option key={n} value={n}>{n} per page</option>)}
            </select>
          )}
        </div>
      </div>

      {/* PLAN summary cards dipindah ke Performance tab (Performance Statistics Plan) per keputusan 2026-07-24 */}
      {/* Plan chart dipindah ke tab Performance (Equity Growth Curve + Drawdown), tidak diduplikasi di sini per keputusan 2026-07-24 */}


      {loading ? (
        <div style={{color:"var(--text-faint)",textAlign:"center",padding:20}}>Loading...</div>
      ) : (
        <div style={{overflowX:"auto"}}>
          <table style={{width:"100%",borderCollapse:"collapse"}}>
            <thead><tr>
              {(isLive
                ? ["Symbol","Type","Lots","Open","Close","Open Price","SL","TP","Close Price","Profit"]
                : ["Symbol","Type","Lots","Open","Close","Open Price","SL","TP","Plan Close","Plan P&L"]
              ).map(h=><th key={h} style={th}>{h}</th>)}
            </tr></thead>
            <tbody>
              {isLive ? liveTrades.map(t=>(
                <tr key={t.id}>
                  <td style={{...td,fontWeight:700}}>{t.symbol}</td>
                  <td style={{...td,color:t.type==="buy"?"var(--success)":"var(--danger)",fontWeight:600}}>{t.type?.toUpperCase()}</td>
                  <td style={td}>{t.lots}</td>
                  <td style={{...td,fontSize:11,color:"var(--text-faint)"}}>{t.open_time?.slice(0,16).replace("T"," ")}</td>
                  <td style={{...td,fontSize:11,color:"var(--text-faint)"}}>{t.close_time?.slice(0,16).replace("T"," ")}</td>
                  <td style={td}>{fPrice(t.open_price)}</td>
                  <td style={{...td,fontSize:11,color:t.sl>0?"var(--danger)":"var(--text-faint)"}}>{t.sl>0?fPrice(t.sl):"—"}</td>
                  <td style={{...td,fontSize:11,color:t.tp>0?"var(--success)":"var(--text-faint)"}}>{t.tp>0?fPrice(t.tp):"—"}</td>
                  <td style={td}>{fPrice(t.close_price)}</td>
                  <td style={{...td,fontWeight:700,color:t.profit>=0?"var(--success)":"var(--danger)"}}>{fMoney(t.profit,{sign:true})}</td>
                </tr>
              )) : planTrades.map((t,i)=>{
                const isMC = t.drawdownPct>=100 && t.runningEquity===0;
                const planExit = t.planResult==="TP" ? t.tp : t.planResult==="SL" ? t.sl : null;
                const planPnl = t.planStatus==="RESOLVED" ? t.planPnl : null;
                return (
                  <tr key={t.ticket||i} style={{opacity:isMC?0.4:1}}>
                    <td style={{...td,fontWeight:700}}>{t.symbol}</td>
                    <td style={{...td,color:t.type==="buy"?"var(--success)":"var(--danger)",fontWeight:600}}>{t.type?.toUpperCase()}</td>
                    <td style={td}>{Number(t.lots||0).toFixed(2)}</td>
                    <td style={{...td,fontSize:11,color:"var(--text-faint)"}}>{t.openTime?.slice(0,16).replace("T"," ").replace("+0000 +0000","")}</td>
                    <td style={{...td,fontSize:11,color:"var(--text-faint)"}}>{t.closeTime?.slice(0,16).replace("T"," ").replace("+0000 +0000","")}</td>
                    <td style={td}>{fPrice(t.openPrice)}</td>
                    <td style={{...td,fontSize:11,color:t.sl>0?"var(--danger)":"var(--text-faint)"}}>{t.sl>0?fPrice(t.sl):"—"}</td>
                    <td style={{...td,fontSize:11,color:t.tp>0?"var(--success)":"var(--text-faint)"}}>{t.tp>0?fPrice(t.tp):"—"}</td>
                    <td style={td}>{planExit?fPrice(planExit):"—"}</td>
                    <td style={{...td,fontWeight:700,color:planPnl===null?"var(--text-faint)":planPnl>=0?"var(--success)":"var(--danger)"}}>
                      {planPnl===null?"—":(planPnl>=0?"+$":"-$")+Math.abs(planPnl).toFixed(2)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {(isLive?liveTrades:planTrades).length===0&&(
            <div style={{textAlign:"center",color:"var(--text-faint)",padding:24}}>No trades found</div>
          )}
          {isLive && totalLive>0&&(
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:14,flexWrap:"wrap",gap:8}}>
              <div style={{fontSize:12,color:"var(--text-faint)"}}>
                Showing {offset+1}–{Math.min(offset+limit,totalLive)} of {totalLive} trades
              </div>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <button onClick={()=>setOffset(0)} disabled={offset===0} style={btnStyle(offset===0)}>«</button>
                <button onClick={()=>setOffset(Math.max(0,offset-limit))} disabled={offset===0} style={btnStyle(offset===0)}>Prev</button>
                <span style={{fontSize:12,color:"var(--text-faint)"}}>Page {Math.floor(offset/limit)+1} / {Math.ceil(totalLive/limit)}</span>
                <button onClick={()=>setOffset(offset+limit)} disabled={offset+limit>=totalLive} style={btnStyle(offset+limit>=totalLive)}>Next</button>
                <button onClick={()=>setOffset((Math.ceil(totalLive/limit)-1)*limit)} disabled={offset+limit>=totalLive} style={btnStyle(offset+limit>=totalLive)}>»</button>
              </div>
            </div>
          )}
          {!isLive && totalPlan>limit&&(
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:14,flexWrap:"wrap",gap:8}}>
              <div style={{fontSize:12,color:"var(--text-faint)"}}>
                Showing {planOffset+1}–{Math.min(planOffset+limit,totalPlan)} of {totalPlan} trades
              </div>
              <div style={{display:"flex",gap:6,alignItems:"center"}}>
                <button onClick={()=>setPlanOffset(0)} disabled={planOffset===0} style={btnStyle(planOffset===0)}>«</button>
                <button onClick={()=>setPlanOffset(Math.max(0,planOffset-limit))} disabled={planOffset===0} style={btnStyle(planOffset===0)}>Prev</button>
                <span style={{fontSize:12,color:"var(--text-faint)"}}>Page {Math.floor(planOffset/limit)+1} / {Math.ceil(totalPlan/limit)}</span>
                <button onClick={()=>setPlanOffset(planOffset+limit)} disabled={planOffset+limit>=totalPlan} style={btnStyle(planOffset+limit>=totalPlan)}>Next</button>
                <button onClick={()=>setPlanOffset((Math.ceil(totalPlan/limit)-1)*limit)} disabled={planOffset+limit>=totalPlan} style={btnStyle(planOffset+limit>=totalPlan)}>»</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function EAKeySection({ accountId }) {
  const [hasKey, setHasKey] = React.useState(false);
  const [newKey, setNewKey] = React.useState("");
  const [generating, setGenerating] = React.useState(false);
  const [error, setError] = React.useState("");

  const token = () => localStorage.getItem("access_token") || localStorage.getItem("token") || "";

  React.useEffect(() => {
    if (!accountId) return;
    fetch("/api/trader/ea-keys", { headers: { Authorization: `Bearer ${token()}` } })
      .then(r => r.json())
      .then(d => { setHasKey((d.keys || []).length > 0); })
      .catch(() => {});
  }, [accountId]);

  const handleGenerate = async () => {
    if (!accountId) return;
    setGenerating(true);
    setError("");
    setNewKey("");
    try {
      const r = await fetch("/api/trader/ea-keys", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ account_id: accountId, platform: "MT5", description: "EA key" }),
      });
      const d = await r.json();
      if (!d.ok) { setError(d.error || "Failed to generate key"); return; }
      setNewKey(d.key);
      setHasKey(true);
    } catch { setError("Network error"); }
    finally { setGenerating(false); }
  };

  return (
    <div style={{padding:"14px",background:"var(--bg-elevated)",borderRadius:"var(--radius-md)",border:"1px solid var(--border)"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:8}}>
        <div>
          <div style={{fontSize:12,fontWeight:600,color:"var(--text-main)"}}>EA Key</div>
          <div style={{fontSize:11,color:"var(--text-muted)",marginTop:2}}>
            {hasKey ? "Key is active — paste into EA input" : "No key yet — generate one"}
          </div>
        </div>
        <button onClick={handleGenerate} disabled={generating || !accountId}
          style={{padding:"6px 14px",borderRadius:"var(--radius-md)",border:"1px solid var(--border)",
            background:"transparent",color:"var(--text-muted)",fontSize:12,fontWeight:600,cursor:"pointer"}}>
          {generating ? "Generating..." : "Generate New Key"}
        </button>
      </div>
      {newKey && (
        <div style={{background:"rgba(245,158,11,0.08)",border:"1px solid rgba(245,158,11,0.3)",
          borderRadius:"var(--radius-md)",padding:"10px 12px",marginTop:8}}>
          <div style={{fontSize:11,fontWeight:600,color:"var(--warning)",marginBottom:6}}>
            Save this key — shown only once
          </div>
          <div style={{display:"flex",gap:8,alignItems:"center"}}>
            <code style={{flex:1,fontSize:11,color:"var(--text-main)",wordBreak:"break-all",
              background:"rgba(0,0,0,0.2)",padding:"6px 10px",borderRadius:"var(--radius-sm)"}}>{newKey}</code>
            <button onClick={()=>{
                if(navigator.clipboard){navigator.clipboard.writeText(newKey).then(()=>alert('Key copied!')).catch(()=>{const el=document.createElement('textarea');el.value=newKey;document.body.appendChild(el);el.select();document.execCommand('copy');document.body.removeChild(el);alert('Key copied!');});}
                else{const el=document.createElement('textarea');el.value=newKey;document.body.appendChild(el);el.select();document.execCommand('copy');document.body.removeChild(el);alert('Key copied!');}
              }}
              style={{padding:"6px 12px",borderRadius:"var(--radius-sm)",border:"none",
                background:"var(--accent)",color:"#fff",fontSize:11,fontWeight:600,cursor:"pointer"}}>
              Copy
            </button>
          </div>
        </div>
      )}
      {hasKey && !newKey && (
        <div style={{padding:"6px 10px",background:"rgba(34,197,94,0.08)",
          borderRadius:"var(--radius-sm)",border:"1px solid rgba(34,197,94,0.2)",marginTop:8}}>
          <span style={{fontSize:11,color:"var(--success)",fontWeight:600}}>EA Key active</span>
          <span style={{fontSize:11,color:"var(--text-faint)",marginLeft:8}}>Generate new key only if compromised</span>
        </div>
      )}
      {error && <div style={{fontSize:12,color:"var(--danger)",marginTop:8}}>{error}</div>}
    </div>
  );
}


function EquityChart({ accountId, currentEquity, currentBalance, mode }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [days, setDays] = React.useState(3650);
  const [hover, setHover] = React.useState(null);
  const svgRef = React.useRef(null);
  React.useEffect(() => {
    if (!accountId) return;
    const load = () => {
      const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
      const url = mode === "PLAN"
        ? `/api/trader/plan-equity-curve?account_id=${accountId}&days=${days}`
        : `/api/public/trader-equity-curve?account_id=${accountId}&days=${days}`;
      fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    };
    setLoading(true);
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [accountId, days, mode]);
  const selStyle = { background:"var(--bg-elevated)",border:"1px solid var(--border)",color:"var(--text-main)",borderRadius:"var(--radius-md)",padding:"5px 10px",fontSize:12 };
  const points = data?.points || [];
  const W=900,H=220,PAD={top:16,right:16,bottom:32,left:64};
  const innerW=W-PAD.left-PAD.right, innerH=H-PAD.top-PAD.bottom;
  // Keep all points including zero equity (margin call shows as 100% DD)
  const validPts=points;
  const equityVals=validPts.map(p=>p.equity), balanceVals=validPts.map(p=>p.balance);
  const maxEquityVals=validPts.map(p=>p.max_equity||p.equity);
  const minEquityVals=validPts.map(p=>p.min_equity||p.equity);
  const allVals=[...maxEquityVals,...balanceVals].filter(v=>v>0);
  const allMinVals=[...minEquityVals].filter(v=>v>0);
  const minV=allMinVals.length?Math.min(...allMinVals)*0.995:0;
  const maxV=allVals.length?Math.max(...allVals)*1.005:1;
  const xOf=i=>PAD.left+(i/Math.max(validPts.length-1,1))*innerW;
  const yOf=v=>PAD.top+innerH-((v-minV)/(maxV-minV||1))*innerH;
  const toPath=vals=>vals.map((v,i)=>`${i===0?"M":"L"}${xOf(i).toFixed(1)},${yOf(v).toFixed(1)}`).join(" ");
  const toFill=vals=>{ if(!vals.length)return""; const base=yOf(minV); return toPath(vals)+` L${xOf(vals.length-1).toFixed(1)},${base} L${xOf(0).toFixed(1)},${base} Z`; };
  const yTickVals=Array.from({length:6},(_,i)=>minV+(maxV-minV)*(i/5));
  const xTickCount=Math.min(6,points.length);
  const xTickIdxs=validPts.length<=1?[0]:Array.from({length:xTickCount},(_,i)=>Math.round(i*(validPts.length-1)/(xTickCount-1)));
  const fmtDate=str=>{ if(!str)return""; const d=new Date(str); return `${d.getDate()}/${d.getMonth()+1}`; };
  const fmtMoney=v=>fmtDollar(v);
  const liveEq=Number(currentEquity)||0; const growth=(data?.start>0&&liveEq>0)?((liveEq-Number(data.start))/Number(data.start)*100):(data?.growth_pct??0);
  const growthColor=growth>=0?"var(--success)":"var(--danger)";
  return (
    <div style={card}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>Equity Growth Curve{mode==="PLAN"?" (Plan)":""}</div>
          <div style={{fontSize:10,color:"var(--text-faint)",marginTop:2}}>{mode==="PLAN"?"Simulated equity if SL/TP plan was followed":"Based on real-time equity incl. floating P&L — may differ from ROI"}</div>
          {data&&points.length>1&&<span style={{fontSize:12,fontWeight:700,padding:"2px 8px",borderRadius:"var(--radius-sm)",background:growth>=0?"var(--success-soft)":"var(--danger-soft)",color:growthColor}}>{growth>=0?"+":""}{growth.toFixed(2)}%</span>}
        </div>
        <div style={{display:"flex",gap:8,alignItems:"center"}}>
          <div style={{display:"flex",gap:10,marginRight:8}}>
            <div style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:12,height:2,background:"var(--accent)",borderRadius:2}}/><span style={{fontSize:11,color:"var(--text-faint)"}}>Equity</span></div>
            <div style={{display:"flex",alignItems:"center",gap:4}}><div style={{width:12,height:2,background:"var(--text-faint)",borderRadius:2,opacity:0.5}}/><span style={{fontSize:11,color:"var(--text-faint)"}}>Balance</span></div>
          </div>
          <select value={days} onChange={e=>setDays(Number(e.target.value))} style={selStyle}>
            <option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option><option value={180}>180 days</option><option value={365}>1 year</option><option value={3650}>All Time</option>
          </select>
        </div>
      </div>
      {loading?<div style={{height:160,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--text-faint)",fontSize:13}}>Loading...</div>:validPts.length<2?<div style={{height:160,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--text-faint)",fontSize:13}}>Not enough data yet</div>:(
        <div style={{position:"relative"}}>
          <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:"auto",display:"block"}}
            onMouseMove={e=>{const rect=svgRef.current?.getBoundingClientRect();if(!rect)return;const x=(e.clientX-rect.left)/rect.width*W;const idx=Math.min(validPts.length-1,Math.max(0,Math.round((x-PAD.left)/innerW*(validPts.length-1))));setHover({idx,x:xOf(idx),point:validPts[idx]});}}
            onMouseLeave={()=>setHover(null)}>
            <defs><linearGradient id="eq-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="var(--accent)" stopOpacity="0.25"/><stop offset="100%" stopColor="var(--accent)" stopOpacity="0.02"/></linearGradient></defs>
            {yTickVals.map((v,i)=>(
              <g key={i}>
                <line x1={PAD.left} y1={yOf(v)} x2={W-PAD.right} y2={yOf(v)} stroke="var(--border-subtle)" strokeWidth="0.5" strokeDasharray="4 4"/>
                <text x={PAD.left-6} y={yOf(v)+4} textAnchor="end" fontSize="10" fill="var(--text-faint)">{fmtMoney(v)}</text>
              </g>
            ))}
            <path d={toFill(balanceVals)} fill="rgba(148,163,184,0.06)"/>
            <path d={toFill(equityVals)} fill="url(#eq-fill)"/>
            <path d={toPath(balanceVals)} fill="none" stroke="var(--text-faint)" strokeWidth="1" strokeDasharray="4 3" opacity="0.5"/>
            <path d={toPath(equityVals)} fill="none" stroke="var(--accent)" strokeWidth="2"/>
            {xTickIdxs.map(i=><text key={i} x={xOf(i)} y={H-6} textAnchor="middle" fontSize="10" fill="var(--text-faint)">{fmtDate(points[i]?.time)}</text>)}
            {hover&&(<>
              <line x1={hover.x} y1={PAD.top} x2={hover.x} y2={H-PAD.bottom} stroke="var(--accent)" strokeWidth="0.8" opacity="0.5"/>
              <circle cx={hover.x} cy={yOf(hover.point.equity)} r="4" fill="var(--accent)" stroke="var(--bg-surface)" strokeWidth="2"/>
              <circle cx={hover.x} cy={yOf(hover.point.balance)} r="3" fill="var(--text-faint)" stroke="var(--bg-surface)" strokeWidth="2"/>
            </>)}
          </svg>
          {hover&&hover.point&&(
            <div style={{position:"absolute",top:8,left:hover.idx>points.length*0.6?"auto":(hover.x/W*100)+"%",right:hover.idx>points.length*0.6?(100-hover.x/W*100)+"%":"auto",background:"var(--bg-elevated)",border:"1px solid var(--border)",borderRadius:"var(--radius-md)",padding:"8px 12px",fontSize:11,pointerEvents:"none",whiteSpace:"nowrap",zIndex:10}}>
              <div style={{color:"var(--text-faint)",marginBottom:4}}>{hover.point.time?new Date(hover.point.time).toLocaleDateString():""}</div>
              <div style={{display:"flex",gap:12}}>
                <div><div style={{color:"var(--text-faint)"}}>Equity</div><div style={{fontWeight:700,color:"var(--accent)"}}>{fmtMoney(hover.point.equity)}</div></div>
                <div><div style={{color:"var(--text-faint)"}}>Balance</div><div style={{fontWeight:600,color:"var(--text-main)"}}>{fmtMoney(hover.point.balance)}</div></div>
                <div><div style={{color:"var(--text-faint)"}}>Float P&L</div><div style={{fontWeight:600,color:hover.point.float_pl>=0?"var(--success)":"var(--danger)"}}>{hover.point.float_pl>=0?"+":""}{fmtMoney(hover.point.float_pl)}</div></div>
              </div>
            </div>
          )}
        </div>
      )}
      {data&&points.length>1&&(
        <div style={{display:"flex",gap:16,marginTop:12,paddingTop:12,borderTop:"1px solid var(--border-subtle)",flexWrap:"wrap",alignItems:"flex-end"}}>
          {[{label:"Start",value:fmtMoney(data.start)},{label:"Current",value:fmtMoney(liveEq>0?liveEq:data.end),color:growthColor},{label:"Peak",value:fmtMoney(data.max_equity)},{label:"Max DD",value:data.max_dd_pct>0?fmtPct(data.max_dd_pct):"—",color:data.max_dd_pct>0?"var(--danger)":"var(--text-faint)"}].map(s=>(
            <div key={s.label}><div style={{fontSize:10,color:"var(--text-faint)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:2}}>{s.label}</div><div style={{fontSize:13,fontWeight:700,color:s.color||"var(--text-main)"}}>{s.value}</div></div>
          ))}
          {points.length>1&&(()=>{
            const first=data.first_trade_date?new Date(data.first_trade_date):new Date(validPts[0].time);
            const last=new Date(validPts[validPts.length-1].time);
            const days=Math.round((last-first)/(1000*60*60*24));
            const fmt=d=>`${d.getDate()}/${d.getMonth()+1}/${d.getFullYear()}`;
            return <div style={{marginLeft:"auto"}}><div style={{fontSize:10,color:"var(--text-faint)",textTransform:"uppercase",letterSpacing:"0.06em",marginBottom:2}}>Trading Period</div><div style={{fontSize:11,fontWeight:600,color:"var(--text-muted)"}}>{fmt(first)} → {fmt(last)} <span style={{color:"var(--accent)"}}>({days} days)</span></div></div>;
          })()}
        </div>
      )}
    </div>
  );
}

function DrawdownChart({ accountId, mode }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [days, setDays] = React.useState(3650);
  const [hover, setHover] = React.useState(null);
  const svgRef = React.useRef(null);
  React.useEffect(() => {
    if (!accountId) return;
    const load = () => {
      const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
      const url = mode === "PLAN"
        ? `/api/trader/plan-equity-curve?account_id=${accountId}&days=${days}`
        : `/api/public/trader-equity-curve?account_id=${accountId}&days=${days}`;
      fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    };
    setLoading(true);
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [accountId, days, mode]);
  const selStyle = { background:"var(--bg-elevated)",border:"1px solid var(--border)",color:"var(--text-main)",borderRadius:"var(--radius-md)",padding:"5px 10px",fontSize:12 };
  const points = data?.points || [];
  const ddSeries = React.useMemo(() => {
    let peak=0;
    return points.map(p=>{
      // Normalize with cumulative withdrawals — WD is not a loss, should not spike DD
      const wd = p.cumulative_wd||0;
      const hi = (p.max_equity||p.equity||0) + wd;
      const lo = (p.min_equity||p.equity||0) + wd;
      if(hi>peak) peak=hi;
      const dd=peak>0?((peak-lo)/peak)*100:0;
      return {time:p.time,dd:-dd,equity:p.equity};
    });
  }, [points]);
  const W=900,H=160,PAD={top:12,right:16,bottom:28,left:52};
  const innerW=W-PAD.left-PAD.right, innerH=H-PAD.top-PAD.bottom;
  const minDD=ddSeries.length?Math.min(...ddSeries.map(d=>d.dd),-1)*1.1:-10;
  const xOf=i=>PAD.left+(i/Math.max(ddSeries.length-1,1))*innerW;
  const yOf=v=>PAD.top+(v/minDD)*innerH;
  const toPath=()=>{ if(!ddSeries.length)return""; return ddSeries.map((d,i)=>`${i===0?"M":"L"}${xOf(i).toFixed(1)},${yOf(d.dd).toFixed(1)}`).join(" "); };
  const toFill=()=>{ if(!ddSeries.length)return""; return toPath()+` L${xOf(ddSeries.length-1).toFixed(1)},${PAD.top} L${xOf(0).toFixed(1)},${PAD.top} Z`; };
  const xTickCount=Math.min(6,ddSeries.length);
  const xTickIdxs=ddSeries.length<=1?[0]:Array.from({length:xTickCount},(_,i)=>Math.round(i*(ddSeries.length-1)/(xTickCount-1)));
  const fmtDate=str=>{ if(!str)return""; const d=new Date(str); return `${d.getDate()}/${d.getMonth()+1}`; };
  const yTicks=[0,-5,-10,-20,-30].filter(v=>v>=minDD);
  const maxDD=data?.max_dd_pct??0;
  const ddColor=maxDD<=10?"var(--success)":maxDD<=20?"var(--warning)":"var(--danger)";
  return (
    <div style={card}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div style={{display:"flex",alignItems:"center",gap:12}}>
          <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>Drawdown — Underwater Curve{mode==="PLAN"?" (Plan)":""}</div>
          {data&&maxDD>=0&&<span style={{fontSize:12,fontWeight:700,padding:"2px 8px",borderRadius:"var(--radius-sm)",background:"var(--danger-soft)",color:ddColor}}>Max DD: {fmtPct(maxDD)}</span>}
        </div>
        <select value={days} onChange={e=>setDays(Number(e.target.value))} style={selStyle}>
          <option value={7}>7 days</option><option value={30}>30 days</option><option value={90}>90 days</option><option value={180}>180 days</option><option value={365}>1 year</option><option value={3650}>All Time</option>
        </select>
      </div>
      {loading?<div style={{height:120,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--text-faint)",fontSize:13}}>Loading...</div>:ddSeries.length<2?<div style={{height:120,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--text-faint)",fontSize:13}}>Not enough data yet</div>:(
        <div style={{position:"relative"}}>
          <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} style={{width:"100%",height:"auto",display:"block"}}
            onMouseMove={e=>{const rect=svgRef.current?.getBoundingClientRect();if(!rect)return;const x=(e.clientX-rect.left)/rect.width*W;const idx=Math.min(ddSeries.length-1,Math.max(0,Math.round((x-PAD.left)/innerW*(ddSeries.length-1))));setHover({idx,x:xOf(idx),point:ddSeries[idx]});}}
            onMouseLeave={()=>setHover(null)}>
            <defs><linearGradient id="dd-fill" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="rgba(239,68,68,0.35)"/><stop offset="100%" stopColor="rgba(239,68,68,0.05)"/></linearGradient></defs>
            {yTicks.map(v=>(
              <g key={v}>
                <line x1={PAD.left} y1={yOf(v)} x2={W-PAD.right} y2={yOf(v)} stroke="var(--border-subtle)" strokeWidth="0.5" strokeDasharray="4 4"/>
                <text x={PAD.left-4} y={yOf(v)+4} textAnchor="end" fontSize="10" fill="var(--text-faint)">{v}%</text>
              </g>
            ))}
            <line x1={PAD.left} y1={PAD.top} x2={W-PAD.right} y2={PAD.top} stroke="var(--border)" strokeWidth="1"/>
            <path d={toFill()} fill="url(#dd-fill)"/>
            <path d={toPath()} fill="none" stroke="var(--danger)" strokeWidth="1.5"/>
            {xTickIdxs.map(i=><text key={i} x={xOf(i)} y={H-6} textAnchor="middle" fontSize="10" fill="var(--text-faint)">{fmtDate(ddSeries[i]?.time)}</text>)}
            {hover&&(<>
              <line x1={hover.x} y1={PAD.top} x2={hover.x} y2={H-PAD.bottom} stroke="var(--danger)" strokeWidth="0.8" opacity="0.4"/>
              <circle cx={hover.x} cy={yOf(hover.point.dd)} r="4" fill="var(--danger)" stroke="var(--bg-surface)" strokeWidth="2"/>
            </>)}
          </svg>
          {hover&&hover.point&&(
            <div style={{position:"absolute",top:8,left:hover.idx>ddSeries.length*0.6?"auto":(hover.x/W*100)+"%",right:hover.idx>ddSeries.length*0.6?(100-hover.x/W*100)+"%":"auto",background:"var(--bg-elevated)",border:"1px solid var(--border)",borderRadius:"var(--radius-md)",padding:"8px 12px",fontSize:11,pointerEvents:"none",whiteSpace:"nowrap",zIndex:10}}>
              <div style={{color:"var(--text-faint)",marginBottom:4}}>{hover.point.time?new Date(hover.point.time).toLocaleDateString():""}</div>
              <div style={{fontWeight:700,color:"var(--danger)"}}>DD: {Math.abs(hover.point.dd).toFixed(2)}%</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


function TradingHeatmap({ accountId, mode }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [view, setView] = React.useState("pnl"); // pnl | trades | winrate
  const [period, setPeriod] = React.useState("3M");

  React.useEffect(() => {
    if (!accountId) return;
    const load = () => {
      const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
      const endpoint = mode === "PLAN" ? "plan-trading-heatmap" : "trading-heatmap";
      fetch(`/api/trader/${endpoint}?account_id=${accountId}&period=${period}`, {
        headers: { Authorization: `Bearer ${token}` },
      }).then(r => r.json()).then(d => { setData(d); setLoading(false); }).catch(() => setLoading(false));
    };
    load();
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [accountId, period, mode]);

  const HOURS = Array.from({length:24},(_,i)=>i);
  const cells = data?.cells || [];
  const bias = data?.bias;
  const now = new Date();
  const rangeStart = new Date(now.getTime() - 30*24*60*60*1000);
  const fmtD = d => `${d.getDate()}/${d.getMonth()+1}/${d.getFullYear()}`;
  // Get unique dates from cells — filtered by period
  const allDates = [...new Set(cells.map(c=>c.date))].sort();
  const periodDays={"1M":30,"3M":90,"6M":180,"1Y":365}[period]||90;
  const cutoff = period==="All" ? "" : new Date(Date.now()-periodDays*86400000).toISOString().slice(0,10);
  const dates = period==="All" ? allDates : allDates.filter(d=>d>=cutoff);
  const fmtDateLabel = d => { const dt=new Date(d); return `${dt.getDate()}/${dt.getMonth()+1}`; };
  const DAY_NAMES = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];

  // Build lookup map
  const cellMap = {};
  cells.forEach(c => { cellMap[`${c.date}-${c.hour}`] = c; });

  // Get value for color scaling
  const getValue = (c) => {
    if (!c) return 0;
    if (view === "pnl") return c.net_pnl;
    if (view === "trades") return c.trades;
    if (view === "winrate") return c.trades > 0 ? c.wins / c.trades * 100 : 0;
    return 0;
  };

  const allVals = cells.map(c => getValue(c)).filter(v => v !== 0);
  const maxVal = allVals.length ? Math.max(...allVals.map(Math.abs)) : 1;

  const getColor = (c) => {
    if (!c || c.trades === 0) return "var(--bg-elevated)";
    const val = getValue(c);
    if (view === "trades") {
      const intensity = Math.min(val / maxVal, 1);
      return `rgba(99,179,237,${0.15 + intensity * 0.7})`;
    }
    if (view === "winrate") {
      const pct = val;
      if (pct >= 60) return `rgba(34,197,94,${0.3 + (pct-60)/40*0.5})`;
      if (pct >= 40) return `rgba(245,158,11,0.3)`;
      return `rgba(239,68,68,0.4)`;
    }
    // pnl
    if (val > 0) {
      const intensity = Math.min(val / maxVal, 1);
      return `rgba(34,197,94,${0.15 + intensity * 0.7})`;
    } else {
      const intensity = Math.min(Math.abs(val) / maxVal, 1);
      return `rgba(239,68,68,${0.15 + intensity * 0.7})`;
    }
  };

  const fmtVal = (c) => {
    if (!c || c.trades === 0) return "";
    if (view === "pnl") {
      const v = c.net_pnl;
      return (v >= 0 ? "+" : "") + fmt2(Math.abs(v));
    }
    if (view === "trades") return c.trades;
    if (view === "winrate") return Math.round(c.wins/c.trades*100)+"%";
    return "";
  };

  const selStyle = {background:"var(--bg-elevated)",border:"1px solid var(--border)",color:"var(--text-main)",borderRadius:"var(--radius-md)",padding:"4px 8px",fontSize:11};

  return (
    <div style={card}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div style={{fontSize:13,fontWeight:600,color:"var(--text-main)"}}>Trading Hours Heatmap{mode==="PLAN"?" (Plan)":""}</div>
        <div style={{display:"flex",gap:4}}>
          {["1M","3M","6M","1Y","All"].map(p=>(
            <button key={p} onClick={()=>setPeriod(p)} style={{padding:"3px 10px",fontSize:11,borderRadius:"var(--radius-full)",border:`1px solid ${period===p?"var(--accent)":"var(--border)"}`,background:period===p?"var(--accent)":"transparent",color:period===p?"#fff":"var(--text-muted)",cursor:"pointer"}}>{p}</button>
          ))}
        </div>
        <select value={view} onChange={e=>setView(e.target.value)} style={selStyle}>
          <option value="pnl">Net P&L</option>
          <option value="trades">Trade Count</option>
          <option value="winrate">Win Rate</option>
        </select>
      </div>

      {loading ? <div style={{padding:40,textAlign:"center",color:"var(--text-faint)"}}>Loading...</div> : (
        <>
          {/* Date range info */}
          <div style={{fontSize:11,color:"var(--text-faint)",marginBottom:10}}>
            P&L by date (UTC) · {fmtD(rangeStart)} → {fmtD(now)} · hover for details
          </div>
          {/* Heatmap grid */}
          <div style={{overflowX:"auto"}}>
            <div style={{display:"grid",gridTemplateColumns:"36px repeat(24,1fr)",gap:2,minWidth:600}}>
              {/* Hour headers */}
              <div style={{fontSize:9,color:"var(--text-faint)",textAlign:"right",paddingRight:4}}>Date</div>
              {HOURS.map(h=>(
                <div key={h} style={{fontSize:9,color:"var(--text-faint)",textAlign:"center",paddingBottom:2}}>
                  {h}
                </div>
              ))}
              {/* Date rows */}
              {dates.map(date=>{
                const dt=new Date(date);
                const dayName=DAY_NAMES[dt.getUTCDay()];
                return (
                <React.Fragment key={date}>
                  <div style={{fontSize:9,color:"var(--text-faint)",display:"flex",flexDirection:"column",alignItems:"flex-end",justifyContent:"center",paddingRight:4,lineHeight:1.2}}>
                    <span style={{fontWeight:600}}>{fmtDateLabel(date)}</span>
                    <span style={{fontSize:8,opacity:0.6}}>{dayName}</span>
                  </div>
                  {HOURS.map(h=>{
                    const c = cellMap[`${date}-${h}`];
                    return (
                      <div key={h} title={c ? `${date} ${h}:00 UTC — ${c.trades} trades, P&L: $${c.net_pnl?.toFixed(2)}, Win: ${c.trades>0?Math.round(c.wins/c.trades*100):0}%` : ""}
                        style={{
                          height:20,borderRadius:"var(--radius-sm)",
                          background:getColor(c),
                          display:"flex",alignItems:"center",justifyContent:"center",
                          fontSize:8,fontWeight:600,
                          color:c&&c.trades>0?"var(--text-main)":"transparent",
                          cursor:c&&c.trades>0?"pointer":"default",
                          border:"1px solid rgba(255,255,255,0.04)",
                        }}>
                        {fmtVal(c)}
                      </div>
                    );
                  })}
                </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* X-axis note */}
          <div style={{fontSize:10,color:"var(--text-faint)",marginTop:6,textAlign:"center"}}>
            Hour (UTC) — hover cell for details
          </div>

          {/* Long vs Short Bias */}
          {bias && bias.total > 0 && (
            <div style={{marginTop:16,paddingTop:14,borderTop:"1px solid var(--border-subtle)"}}>
              <div style={{fontSize:12,fontWeight:600,color:"var(--text-main)",marginBottom:10}}>Long vs Short Bias</div>
              <div style={{display:"flex",gap:12,alignItems:"center",flexWrap:"wrap"}}>
                {/* Bar */}
                <div style={{flex:1,minWidth:200}}>
                  <div style={{display:"flex",height:10,borderRadius:"var(--radius-sm)",overflow:"hidden",marginBottom:6}}>
                    <div style={{width:`${bias.buy_pct}%`,background:"var(--success)",transition:"width 0.5s"}}/>
                    <div style={{width:`${bias.sell_pct}%`,background:"var(--danger)",transition:"width 0.5s"}}/>
                  </div>
                  <div style={{display:"flex",justifyContent:"space-between"}}>
                    <span style={{fontSize:11,color:"var(--success)",fontWeight:600}}>Long {fmtPct(bias.buy_pct)}</span>
                    <span style={{fontSize:11,color:"var(--danger)",fontWeight:600}}>Short {fmtPct(bias.sell_pct)}</span>
                  </div>
                </div>
                {/* Stats */}
                <div style={{display:"flex",gap:16}}>
                  <div>
                    <div style={{fontSize:10,color:"var(--text-faint)",marginBottom:2}}>Buy Trades</div>
                    <div style={{fontSize:13,fontWeight:700,color:"var(--success)"}}>{bias.buy_trades}</div>
                    <div style={{fontSize:10,color:"var(--text-faint)"}}>{fmtDollar(bias.buy_pnl)} P&L</div>
                  </div>
                  <div>
                    <div style={{fontSize:10,color:"var(--text-faint)",marginBottom:2}}>Sell Trades</div>
                    <div style={{fontSize:13,fontWeight:700,color:"var(--danger)"}}>{bias.sell_trades}</div>
                    <div style={{fontSize:10,color:"var(--text-faint)"}}>{fmtDollar(bias.sell_pnl)} P&L</div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function TraderOnboarding({ accountId, connectionStatus, onGoToAccounts }) {
  const [hasKey, setHasKey] = React.useState(null); // null = checking
  const [dismissed, setDismissed] = React.useState(
    () => localStorage.getItem("trader_onboarding_v1") === "1"
  );

  React.useEffect(() => {
    if (!accountId) return;
    const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
    fetch("/api/trader/ea-keys", { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setHasKey((d.keys || []).length > 0))
      .catch(() => setHasKey(false));
  }, [accountId]);

  const hasAutoTrading = connectionStatus === "connected";

  const allRequired = hasKey === true && hasAutoTrading;
  React.useEffect(() => {
    if (allRequired) { localStorage.setItem("trader_onboarding_v1", "1"); setDismissed(true); }
  }, [allRequired]);

  if (dismissed || hasKey === null) return null;
  if (hasKey === true && hasAutoTrading) return null;

  const steps = [
    {
      n: 1,
      title: "Download & install the EA",
      desc: "Copy the EA file (.ex4 or .ex5) to your MetaTrader Experts folder.",
      done: null,
      required: false,
      action: { label: "Go to Accounts →", fn: onGoToAccounts },
      locked: false,
    },
    {
      n: 2,
      title: "Generate your EA Key",
      desc: "Create a unique API key from the Accounts tab and paste it into the EA inputs.",
      done: hasKey,
      required: true,
      action: !hasKey ? { label: "Go to Accounts →", fn: onGoToAccounts } : null,
      locked: false,
    },
    {
      n: 3,
      title: "Enable AutoTrading",
      desc: "Turn on AutoTrading in MT4/MT5 and attach the EA to any chart. Your trades will sync automatically.",
      done: hasAutoTrading,
      required: true,
      action: null,
      locked: !hasKey,
    },
  ];

  const trackedSteps = steps.filter(s => s.required);
  const doneCount = trackedSteps.filter(s => s.done).length;
  const pct = Math.round(doneCount / trackedSteps.length * 100);

  return (
    <div style={{
      background: "var(--bg-surface)", border: "1px solid var(--border)",
      borderLeft: "3px solid var(--accent)", borderRadius: "var(--radius-xl)",
      padding: "24px 28px", marginBottom: 20,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div>
          <div style={{ fontSize: 17, fontWeight: 800, color: "var(--text-main)", marginBottom: 4 }}>
            Finish setting up your EA
          </div>
          <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
            Complete these steps to start syncing live trades automatically.
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: 20, fontWeight: 900, color: "var(--accent)" }}>{doneCount}/{trackedSteps.length}</div>
            <div style={{ fontSize: 10, color: "var(--text-muted)" }}>steps done</div>
          </div>
          <button onClick={() => { localStorage.setItem("trader_onboarding_v1", "1"); setDismissed(true); }}
            style={{ padding: "4px 12px", fontSize: 11, borderRadius: "var(--radius-md)",
              border: "1px solid var(--border)", background: "transparent",
              color: "var(--text-muted)", cursor: "pointer" }}>
            Dismiss
          </button>
        </div>
      </div>

      <div style={{ height: 4, background: "var(--border)", borderRadius: 2, marginBottom: 20 }}>
        <div style={{ height: "100%", width: `${pct}%`, background: "var(--accent)", borderRadius: 2, transition: "width 0.6s ease" }} />
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {steps.map(s => (
          <div key={s.n} style={{
            display: "flex", gap: 14, alignItems: "center", padding: "14px 16px",
            background: s.done ? "var(--success-soft)" : s.locked ? "rgba(255,255,255,0.01)" : "var(--bg-elevated)",
            border: `1px solid ${s.done ? "rgba(34,197,94,0.25)" : "var(--border)"}`,
            borderRadius: "var(--radius-md)",
            opacity: s.locked ? 0.45 : 1,
            pointerEvents: s.locked ? "none" : "auto",
          }}>
            <div style={{
              width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
              background: s.done ? "var(--success)" : "var(--bg-elevated)",
              border: s.done ? "none" : "2px solid var(--border)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: s.done ? 14 : 12, fontWeight: 800,
              color: s.done ? "#fff" : "var(--text-muted)",
            }}>
              {s.done ? "✓" : s.n}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 3, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 700,
                  color: s.done ? "var(--text-muted)" : "var(--text-main)",
                  textDecoration: s.done ? "line-through" : "none" }}>
                  {s.title}
                </span>
                {!s.done && s.required && (
                  <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 0.6,
                    color: "var(--accent)", background: "rgba(78,154,241,0.12)",
                    padding: "1px 6px", borderRadius: "var(--radius-full)", border: "1px solid rgba(78,154,241,0.25)" }}>
                    REQUIRED
                  </span>
                )}
                {!s.required && (
                  <span style={{ fontSize: 9, fontWeight: 600, letterSpacing: 0.6,
                    color: "var(--text-muted)", background: "rgba(255,255,255,0.06)",
                    padding: "1px 6px", borderRadius: "var(--radius-full)" }}>
                    OPTIONAL
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5 }}>{s.desc}</div>
            </div>
            {s.done ? (
              <span style={{ fontSize: 12, color: "var(--success)", fontWeight: 700, flexShrink: 0 }}>✓ Done</span>
            ) : s.locked ? (
              <span style={{ fontSize: 10, color: "var(--text-muted)", flexShrink: 0 }}>🔒 Step {s.n - 1} first</span>
            ) : s.action ? (
              <button onClick={s.action.fn} style={{
                padding: "7px 16px", borderRadius: "var(--radius-md)", border: "none",
                background: "var(--accent)", color: "#fff",
                fontSize: 12, fontWeight: 700, cursor: "pointer", flexShrink: 0, whiteSpace: "nowrap",
              }}>
                {s.action.label}
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TraderDashboard() {
  usePageTitle("Trader Dashboard");
  const { displayConfig, appName } = usePartner();
  const [searchParams, setSearchParams] = useSearchParams();
  const [accounts, setAccounts] = useState([]);
  const [alphaData, setAlphaData] = useState(null);
  const [perPairData, setPerPairData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [mode, setMode] = useState("LIVE");
  const [planData, setPlanData] = useState(null);
  const [planAlphaData, setPlanAlphaData] = useState(null);
  const [followers, setFollowers] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editAccount, setEditAccount] = useState(null);
  const [editNickname, setEditNickname] = useState("");
  const [editAbout, setEditAbout] = useState("");
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  const [eaExpanded, setEaExpanded] = useState(false);
  const [traderNewKey, setTraderNewKey] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    api.getAccounts().then(d => { setAccounts(d || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const [profileComplete, setProfileComplete] = useState(null); // null = checking, true/false after check

  useEffect(() => {
    const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
    Promise.all([
      fetch("/api/profile", { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => ({})),
      fetch("/api/profile/preferences", { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json()).catch(() => ({})),
    ]).then(([profileData, prefsData]) => {
      const instruments = prefsData?.preferences?.preferred_instruments || [];
      const complete = !!profileData?.name && !!profileData?.country && instruments.length > 0;
      setProfileComplete(complete);
    });
  }, []);

  useEffect(() => {
    if (tab !== "followers" && tab !== "overview") return;
    const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
    fetch("/api/trader/my-followers", { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => { if (d.ok) setFollowers(d); });
  }, [tab]);

  const selectedAccountId = searchParams.get("account_id");
  const selectedAccount = useMemo(() => {
    if (!accounts.length) return null;
    if (!selectedAccountId) return accounts[0];
    return accounts.find(a => (a.ID || a.id) === selectedAccountId) || accounts[0];
  }, [selectedAccountId, accounts]);

  const accountId = selectedAccount?.ID || selectedAccount?.id;
  useEffect(() => {
    if (!accountId) return;
    const tok = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
    fetch(`/api/trader/trades-plan?account_id=${accountId}&limit=500`, {
      headers: { Authorization: `Bearer ${tok}` }
    }).then(r => r.ok ? r.json() : null).then(d => { if (d) setPlanData(d); });
  }, [accountId]);

  useEffect(() => {
    if (!selectedAccountId && accounts.length > 0) {
      setSearchParams({ account_id: accounts[0].ID || accounts[0].id });
    }
  }, [selectedAccountId, accounts]);

  useEffect(() => {
    if (!accountId) return;
    const loadDashboard = () => {
      const tok = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
      fetch(`/api/trader/account-summary?account_id=${accountId}`, { headers: { Authorization: `Bearer ${tok}` } })
        .then(r => r.json()).then(d => { if (d.ok) setSummary(d); });
      Promise.all([
        api.getAlphaRank(accountId).catch(() => null),
        api.getAlphaRankPerPair(accountId).catch(() => null),
      ]).then(([alpha, perPair]) => {
        setAlphaData(alpha);
        setPerPairData(perPair?.pairs ? perPair : null);
      });
    };
    loadDashboard();
    const interval = setInterval(loadDashboard, 30000);
    return () => clearInterval(interval);
  }, [accountId]);
  useEffect(() => {
    if (!accountId || mode !== "PLAN") return;
    api.getPlanAlphaRank(accountId).catch(() => null).then(d => setPlanAlphaData(d));
  }, [accountId, mode]);

  const snap = alphaData?.snapshot;
  const stats = alphaData?.statistics;
  const pillars = Array.isArray(alphaData?.pillars) ? alphaData.pillars : [];
  const flags = alphaData?.riskFlags;
  const planSnap = planAlphaData?.alphaRank;
  const planPillars = Array.isArray(planSnap?.pillars) ? planSnap.pillars : [];
  const planFlags = planSnap?.riskFlags;

  const handleEditSave = async () => {
    if (!editAccount) return;
    setEditLoading(true);
    setEditError("");
    try {
      const token = localStorage.getItem("access_token") || localStorage.getItem("token");
      const res = await fetch(`/api/trader/accounts/${editAccount.ID || editAccount.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ nickname: editNickname, about: editAbout }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to update");
      setEditAccount(null);
      api.getAccounts().then(d => setAccounts(d || []));
    } catch (err) {
      setEditError(err.message);
    } finally {
      setEditLoading(false);
    }
  };

  const handleDelete = async (targetId) => {
    const delId = targetId || accountId;
    if (!delId || !window.confirm("Delete this account? All trade data and AlphaRank will be permanently removed.")) return;
    const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
    const r = await fetch(`/api/trader/accounts/${delId}`, { method: "DELETE", headers: { Authorization: `Bearer ${token}` } });
    if (r.ok) { window.location.reload(); } else { alert("Delete failed"); }
  };

  const selStyle = {
    background: "var(--bg-elevated)", border: "1px solid var(--border)",
    color: "var(--text-main)", borderRadius: "var(--radius-md)", padding: "8px 12px", fontSize: 13,
  };

  if (loading) return (
    <div style={{ padding: 40, textAlign: "center", color: "var(--text-faint)" }}>Loading...</div>
  );

  if (!selectedAccount) return (
    <div style={{ maxWidth: 600, margin: "40px auto", padding: "0 24px" }}>
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <div style={{ fontSize: 22, fontWeight: 700, color: "var(--text-main)", marginBottom: 8 }}>Welcome to {appName} </div>
        <div style={{ fontSize: 14, color: "var(--text-muted)" }}>Get started in 4 easy steps to track and share your trading performance.</div>
      </div>
      {[
        { n: 0, title: "Complete your profile", desc: "Add your display name, country, and preferred trading instruments — required so investors can find and trust you.", action: "profile" },
        { n: 1, title: "Connect your broker account", desc: "Add your MT4/MT5 account number, server, and investor password (read-only).", action: "account" },
        { n: 2, title: "Download & install the EA", desc: "Copy the EA file (.ex4 or .ex5) to your MetaTrader Experts folder." },
        { n: 3, title: "Generate your EA Key", desc: "Create a unique API key from the Accounts tab and paste it into the EA inputs." },
        { n: 4, title: "Enable AutoTrading", desc: "Turn on AutoTrading in MT4/MT5, attach the EA to any chart, and your trades will sync automatically." },
      ].map((s, i) => {
        const isDone = s.n === 0 ? profileComplete === true : false;
        const isActive = s.n === 0 ? profileComplete !== true : (s.n === 1 && profileComplete === true);
        return (
        <div key={s.n} style={{ display: "flex", gap: 16, marginBottom: 20,
          background: "var(--bg-surface)", border: "1px solid var(--border)",
          borderRadius: "var(--radius-xl)", padding: "16px 20px",
          borderLeft: isActive ? "3px solid var(--accent)" : isDone ? "3px solid var(--success)" : "3px solid var(--border)" }}>
          <div style={{ width: 32, height: 32, borderRadius: "50%", flexShrink: 0,
            background: isDone ? "var(--success)" : isActive ? "var(--accent)" : "var(--bg-elevated)",
            border: "1px solid var(--border)",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 13, fontWeight: 700, color: (isDone || isActive) ? "#fff" : "var(--text-muted)" }}>
            {isDone ? "✓" : s.n}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-main)", marginBottom: 4 }}>{s.title}</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.6 }}>{s.desc}</div>
            {s.action === "profile" && !isDone && (
              <button onClick={() => { window.location.href = "/profile"; }} style={{ marginTop: 10, padding: "7px 18px",
                background: "var(--accent)", color: "#fff", border: "none",
                borderRadius: "var(--radius-md)", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                Complete Profile
              </button>
            )}
            {s.action === "account" && (
              <>
                <button onClick={() => setShowAddModal(true)} disabled={profileComplete !== true}
                  style={{ marginTop: 10, padding: "7px 18px",
                    background: profileComplete === true ? "var(--accent)" : "var(--bg-elevated)",
                    color: profileComplete === true ? "#fff" : "var(--text-faint)",
                    border: profileComplete === true ? "none" : "1px solid var(--border)",
                    borderRadius: "var(--radius-md)", fontSize: 12, fontWeight: 600,
                    cursor: profileComplete === true ? "pointer" : "not-allowed" }}>
                  + Add Account
                </button>
                {profileComplete === false && (
                  <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 6 }}>
                    Complete your profile first to unlock this step.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      );})}
      <div style={{ background: "var(--accent-soft)", border: "1px solid var(--accent-border)",
        borderRadius: "var(--radius-xl)", padding: "14px 20px", fontSize: 12, color: "var(--accent)", textAlign: "center" }}>
           Need a broker? <a href="/brokers" style={{ color: "var(--accent)", fontWeight: 600 }}>View our partner brokers</a> — open an account and start trading on {appName}.
      </div>
      <AddAccountModal isOpen={showAddModal} onClose={() => setShowAddModal(false)}
        onAccountAdded={() => { setShowAddModal(false); window.location.reload(); }} />
    </div>
  );

  const tabs = [
    { id: "overview",    label: "Overview" },
    { id: "performance", label: "Performance" },
    { id: "live",        label: "Live Positions" },
    { id: "history",     label: "Trade History" },
    { id: "followers",   label: "Followers" },
    { id: "accounts",    label: "Accounts" },
  ];

  return (
    <div style={{ padding: "24px 0", maxWidth: 1200, margin: "0 auto" }}>

      {/* Header */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--text-main)", margin: 0 }}>Trader Dashboard</h1>
          <p style={{ color: "var(--text-muted)", fontSize: 13, margin: "4px 0 0" }}>
            Real-time performance analytics & AlphaRank
          </p>
          {selectedAccount?.about && (
            <div style={{ fontSize: 12, color: "var(--text-muted)", fontStyle: "italic", marginTop: 6 }}>
              <strong style={{ color: "var(--text-main)", fontStyle: "normal" }}>Strategy:</strong> {selectedAccount.about}
            </div>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <select value={accountId} onChange={e => setSearchParams({ account_id: e.target.value })} style={selStyle}>
            {accounts.map(a => (
              <option key={a.ID || a.id} value={a.ID || a.id}>
                {a.account_number} · {a.platform} · {a.broker}
              </option>
            ))}
          </select>
          <button onClick={() => setShowAddModal(true)} style={{ padding: "8px 16px",
            background: "var(--accent)", color: "#fff", border: "none",
            borderRadius: "var(--radius-md)", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
            Add Account
          </button>
        </div>
      </div>

      <TraderOnboarding accountId={accountId} connectionStatus={selectedAccount?.connection_status} onGoToAccounts={() => setTab("accounts")} />

      <BecomeAnalystCard accountId={accountId} />
      {tab === "overview" && (
        <div style={{ marginBottom: 14 }}>
          {/* ── AlphaRank Hero (product core) ── */}
          <div style={{ ...card, marginBottom: 0, padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: "var(--text-main)" }}>AlphaRank</div>
                <div style={{fontSize:11,color:"var(--text-faint)",marginTop:2}}>Score only — Marketplace rank depends on sort (AlphaScore, ROI, DD, AUM)</div>
              <button type="button" onClick={() => setTab("performance")}
                style={{ background: "transparent", border: "1px solid var(--border)", borderRadius: "var(--radius-md)",
                  padding: "4px 10px", fontSize: 11, fontWeight: 600, color: "var(--accent)", cursor: "pointer" }}>
                Full analysis →
              </button>
            </div>
            {!snap?.grade ? (
              <div style={{ background: "var(--warning-soft)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: "var(--radius-md)", padding: "12px 14px" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--warning)", marginBottom: 4 }}>AlphaRank not available yet</div>
                <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>Minimum 20 closed trades required. Score appears automatically once the threshold is reached.</div>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "auto minmax(0,1fr) minmax(140px,0.4fr)", gap: 16, alignItems: "start" }}>
                <div>
                  <GradeCircle score={snap?.alphaScore} grade={snap?.grade} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "var(--text-faint)", fontWeight: 600, marginBottom: 8, letterSpacing: "0.04em" }}>TOP PILLARS</div>
                  {[...pillars].sort((a, b) => (Number(b.score) || 0) - (Number(a.score) || 0)).slice(0, 3).map(p => (
                    <PillarBar key={p.code || p.name} name={p.name} score={p.score} reason={p.reason} />
                  ))}
                  {pillars.length === 0 && (
                    <div style={{ fontSize: 12, color: "var(--text-faint)" }}>Pillar detail on Performance tab</div>
                  )}
                </div>
                <div style={{ background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", padding: "10px 12px" }}>
                  <div style={{ fontSize: 11, color: "var(--text-faint)", fontWeight: 600, marginBottom: 6 }}>RISK SUMMARY</div>
                  {flags?.counts?.total > 0 ? (
                    <>
                      <div style={{ fontSize: 16, fontWeight: 700, color: "var(--danger)", marginBottom: 6 }}>
                        {flags.counts.total} Risk Flag{flags.counts.total > 1 ? "s" : ""}
                      </div>
                      {flags.items?.slice(0, 2).map((f, i) => (
                        <div key={i} style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 4, borderLeft: `3px solid ${sevColor(f.severity)}`, paddingLeft: 8 }}>
                          {f.title}
                        </div>
                      ))}
                    </>
                  ) : (
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--success)" }}>No risk flags</div>
                  )}
                  {snap?.riskLevel && (
                    <div style={{ marginTop: 10, fontSize: 12 }}>
                      <span style={{ color: "var(--text-faint)" }}>Level: </span>
                      <span style={{ fontWeight: 700, color: riskColor(snap.riskLevel) }}>{snap.riskLevel}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

        </div>
      )}
      {/* Stats Bar — Primary (5 priority metrics) — Overview only (avoid double on other tabs) */}
      {tab === "overview" && (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(0, 1fr))", gap: 12, marginBottom: 12 }}>
        <StatCard label="Equity" value={fMoney(snap?.equity)}
          sub={snap?.dailyEquityChangePct != null ? `${snap.dailyEquityChangePct>=0?"+":""}${snap.dailyEquityChangePct.toFixed(2)}% today` : "Current equity"}
          subColor={snap?.dailyEquityChangePct > 0 ? "var(--success)" : snap?.dailyEquityChangePct < 0 ? "var(--danger)" : undefined} />
        <StatCard label="Net P&L" value={fMoney(summary?.netProfit, { sign: true })} sub="Total return"
          color={summary?.netProfit >= 0 ? "var(--success)" : "var(--danger)"} />
        <div title="ROI includes floating P&L on open positions (real-time). Public Leaderboard/Marketplace ROI is closed-only.">
          <StatCard label="ROI" value={fPct(summary?.roi, { sign: true })} sub="Return (incl. floating)"
            color={summary?.roi >= 0 ? "var(--success)" : "var(--danger)"} />
        </div>
        <StatCard label="Max Drawdown" value={snap?.maxDD>=0?fPct(snap?.maxDD):"—"} sub="Historical"
          color={snap?.maxDD>20?"var(--danger)":snap?.maxDD>10?"var(--warning)":snap?.maxDD>=0?"var(--success)":undefined} />
        <StatCard label="Win Rate" value={fPct(snap?.winRate)} sub="Overall" />
      </div>
      )}
      {/* Tabs + Global LIVE/PLAN toggle */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid var(--border-subtle)", marginBottom: 20, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 2, overflowX: "auto", overflowY: "hidden" }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)} style={{
              padding: "10px 18px", background: "transparent", border: "none",
              borderBottom: tab === t.id ? "2px solid var(--accent)" : "2px solid transparent",
              color: tab === t.id ? "var(--accent)" : "var(--text-muted)",
              fontWeight: tab === t.id ? 600 : 400, fontSize: 13, cursor: "pointer",
              marginBottom: -1, whiteSpace: "nowrap", transition: "color 0.15s",
            }}>{t.label}</button>
          ))}
        </div>
        <div style={{display:"flex",background:"var(--bg-elevated)",border:"0.5px solid var(--border)",borderRadius:"var(--radius-md)",padding:2,gap:2,marginBottom:6}}>
          {["LIVE","PLAN"].map(m=>(
            <button key={m} onClick={()=>setMode(m)}
              style={{padding:"4px 14px",borderRadius:"var(--radius-sm)",border:"none",cursor:"pointer",fontSize:11,fontWeight:700,
                background:mode===m?"var(--accent)":"transparent",
                color:mode===m?"#fff":"var(--text-muted)",transition:"all 0.15s"}}>
              {m}
            </button>
          ))}
        </div>
      </div>

      {/* Tab: Overview */}
      {tab === "overview" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
            <div style={{ ...card, marginBottom: 0, padding: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 6 }}>Open Positions</div>
              <LivePositions accountId={accountId} />
            </div>
            <div style={{ ...card, marginBottom: 0, padding: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 8 }}>Top Investors</div>
              {!followers ? (
                <div style={{ textAlign: "center", color: "var(--text-faint)", padding: 12, fontSize: 12 }}>Loading...</div>
              ) : followers.followers?.filter((f) => f.status === "ACTIVE").length === 0 ? (
                <div style={{ textAlign: "center", color: "var(--text-faint)", padding: 12, fontSize: 12 }}>No investors yet</div>
              ) : (
                [...followers.followers]
                  .filter((f) => f.status === "ACTIVE")
                  .sort((a, b) => (b.closedProfit ?? 0) - (a.closedProfit ?? 0))
                  .slice(0, 5)
                  .map((f, i, arr) => (
                    <div key={f.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "8px 0", borderBottom: i < arr.length - 1 ? "1px solid var(--border-subtle)" : "none" }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-main)" }}>{f.followerNickname || f.followerAccountId?.slice(0, 8)}</div>
                        <div style={{ fontSize: 11, color: "var(--text-faint)" }}>Since {f.createdAt?.slice(0, 10)}</div>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: (f.closedProfit ?? 0) >= 0 ? "var(--success)" : "var(--danger)" }}>
                        {f.closedProfit != null ? fMoney(f.closedProfit) : "—"}
                      </div>
                    </div>
                  ))
              )}
            </div>
          </div>
        </div>
      )}
      {tab === "performance" && (
        <div>
{mode === "LIVE" && (
          <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12, marginBottom: 12 }}>
            {/* AlphaRank detail — no large GradeCircle (shown on Overview) */}
            <div style={{ ...card, marginBottom: 0, padding: 14 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-main)" }}>AlphaRank Analysis</div>
                {snap?.grade && (
                  <div style={{ fontSize: 12, fontWeight: 700, color: gradeColor(snap.grade) }}>
                    {fmt2(snap?.alphaScore)} · {snap?.grade}
                  </div>
                )}
              </div>
              {!snap?.grade && (
                <div style={{ background: "var(--warning-soft)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: "var(--radius-md)", padding: "12px 14px", marginBottom: 12 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--warning)", marginBottom: 4 }}>AlphaRank not available yet</div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 }}>Minimum 20 closed trades required. Score appears once the threshold is reached.</div>
                </div>
              )}
              <div>
                {pillars.map(p => <PillarBar key={p.code} name={p.name} score={p.score} reason={p.reason} />)}
              </div>
            </div>

            {/* Risk Flags */}
            {!(displayConfig?.modules?.risk_score?.visibility === "hidden") && (
            <div style={card}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-main)" }}>Risk Flags</div>
                {flags?.counts?.total > 0 && (
                  <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: "var(--radius-sm)",
                    background: "var(--danger-soft)", color: "var(--danger)" }}>
                    {flags.counts.total} detected
                  </span>
                )}
              </div>
              {flags?.items?.length > 0 ? flags.items.map((f, i) => (
                <div key={i} style={{ background: "var(--bg-elevated)", borderRadius: "var(--radius-md)",
                  padding: "10px 14px", marginBottom: 8,
                  borderLeft: `3px solid ${sevColor(f.severity)}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-main)" }}>{f.title}</span>
                    <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px",
                      borderRadius: "var(--radius-sm)", background: sevBg(f.severity), color: sevColor(f.severity) }}>
                      {f.severity}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>{f.desc}</div>
                </div>
              )) : (
                <div style={{ textAlign: "center", padding: 30, color: "var(--text-faint)" }}>
                  <div style={{ fontSize: 13, marginTop: 8 }}>No risk flags detected</div>
                </div>
              )}

              {/* Risk Level */}
              {snap?.riskLevel && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ background: riskBg(snap.riskLevel), border: `1px solid ${riskColor(snap.riskLevel)}44`,
                    borderRadius: "var(--radius-md)", padding: "10px 14px" }}>
                    <div style={{ fontSize: 11, color: "var(--text-faint)", marginBottom: 4 }}>Risk Level</div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: riskColor(snap.riskLevel) }}>
                      {snap.riskLevel}
                    </div>
                  </div>
                </div>
              )}

              {/* Risk Profile */}
              {snap?.riskProfile && snap.riskProfile !== "Unknown" && (
                <div style={{ marginTop: 12 }}>
                  <div style={{ background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", padding: "10px 14px" }}>
                    <div style={{ fontSize: 11, color: "var(--text-faint)", marginBottom: 4 }}>Position Sizing Style</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color:
                        snap.riskProfile === "Conservative" ? "var(--success)" :
                        snap.riskProfile === "Balanced" ? "var(--accent)" :
                        snap.riskProfile === "Aggressive" ? "var(--warning)" :
                        snap.riskProfile === "High Risk" ? "#fb923c" : "var(--danger)" }}>
                        {snap.riskProfile}
                      </div>
                      <div style={{ fontSize: 11, color: "var(--text-faint)" }}>
                        avg {fmtPct(snap.avgRiskPct)} P&L per trade
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Survivability + Scalability */}
              <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                <div style={{ flex: 1, background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", padding: "10px 12px" }}>
                  <div style={{ fontSize: 11, color: "var(--text-faint)", marginBottom: 4 }}>Survivability</div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "var(--accent)" }}>{alphaData?.survivability?.score || 0}</div>
                    <div style={{ fontSize: 11, color: "var(--text-faint)" }}>/100</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-main)" }}>{alphaData?.survivability?.label}</div>
                  </div>
                  <div style={{ fontSize: 10, color: "var(--text-faint)", marginTop: 2 }}>{alphaData?.survivability?.note}</div>
                </div>
                <div style={{ flex: 1, background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", padding: "10px 12px" }}>
                  <div style={{ fontSize: 11, color: "var(--text-faint)", marginBottom: 4 }}>Scalability</div>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: "var(--warning)" }}>{alphaData?.scalability?.score || 0}</div>
                    <div style={{ fontSize: 11, color: "var(--text-faint)" }}>/100</div>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-main)" }}>{alphaData?.scalability?.label}</div>
                  </div>
                  <div style={{ fontSize: 10, color: "var(--text-faint)", marginTop: 2 }}>{alphaData?.scalability?.note}</div>
                </div>
              </div>
            </div>
            )}
          </div>
          </>
          )}
          {mode === "PLAN" && (
          <div>
            {!planSnap && (
              <div style={card}>
                <div style={{ textAlign: "center", padding: 30, color: "var(--text-faint)", fontSize: 13 }}>
                  {planAlphaData?.message || "No resolved Plan trades yet."}
                </div>
              </div>
            )}
            {planSnap && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 16, marginBottom: 16 }}>
              {/* Plan AlphaRank */}
              <div style={card}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 16 }}>AlphaRank Analysis (Plan)</div>
                <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
                  <GradeCircle score={planSnap?.alphaScore} grade={planSnap?.grade} />
                  <div style={{ flex: 1 }}>
                    {planPillars.map(p => <PillarBar key={p.code} name={p.name} score={p.score} reason={p.reason} />)}
                  </div>
                </div>
              </div>

              {/* Plan Risk Flags */}
              <div style={card}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-main)" }}>Risk Flags (Plan)</div>
                  {planFlags?.counts?.total > 0 && (
                    <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: "var(--radius-sm)",
                      background: "var(--danger-soft)", color: "var(--danger)" }}>
                      {planFlags.counts.total} detected
                    </span>
                  )}
                </div>
                {planFlags?.items?.length > 0 ? planFlags.items.map((f, i) => (
                  <div key={i} style={{ background: "var(--bg-elevated)", borderRadius: "var(--radius-md)",
                    padding: "10px 14px", marginBottom: 8,
                    borderLeft: `3px solid ${sevColor(f.severity)}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-main)" }}>{f.title}</span>
                      <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 7px",
                        borderRadius: "var(--radius-sm)", background: sevBg(f.severity), color: sevColor(f.severity) }}>
                        {f.severity}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>{f.desc}</div>
                  </div>
                )) : (
                  <div style={{ textAlign: "center", padding: 30, color: "var(--text-faint)" }}>
                    <div style={{ fontSize: 13, marginTop: 8 }}>No risk flags detected</div>
                  </div>
                )}

                {/* Risk Level */}
                {planSnap?.riskLevel && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ background: riskBg(planSnap.riskLevel), border: `1px solid ${riskColor(planSnap.riskLevel)}44`,
                      borderRadius: "var(--radius-md)", padding: "10px 14px" }}>
                      <div style={{ fontSize: 11, color: "var(--text-faint)", marginBottom: 4 }}>Risk Level</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: riskColor(planSnap.riskLevel) }}>
                        {planSnap.riskLevel}
                      </div>
                    </div>
                  </div>
                )}

                {/* Risk Profile */}
                {planSnap?.riskProfile?.label && planSnap.riskProfile.label !== "Unknown" && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", padding: "10px 14px" }}>
                      <div style={{ fontSize: 11, color: "var(--text-faint)", marginBottom: 4 }}>Position Sizing Style</div>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color:
                          planSnap.riskProfile.label === "Conservative" ? "var(--success)" :
                          planSnap.riskProfile.label === "Balanced" ? "var(--accent)" :
                          planSnap.riskProfile.label === "Aggressive" ? "var(--warning)" :
                          planSnap.riskProfile.label === "High Risk" ? "#fb923c" : "var(--danger)" }}>
                          {planSnap.riskProfile.label}
                        </div>
                        <div style={{ fontSize: 11, color: "var(--text-faint)" }}>
                          avg {fmtPct(planSnap.riskProfile.avgRiskPct)} P&L per trade
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Survivability + Scalability */}
                <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
                  <div style={{ flex: 1, background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", padding: "10px 12px" }}>
                    <div style={{ fontSize: 11, color: "var(--text-faint)", marginBottom: 4 }}>Survivability</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: "var(--accent)" }}>{planSnap?.survivability?.score || 0}</div>
                      <div style={{ fontSize: 11, color: "var(--text-faint)" }}>/100</div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-main)" }}>{planSnap?.survivability?.label}</div>
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-faint)", marginTop: 2 }}>{planSnap?.survivability?.note}</div>
                  </div>
                  <div style={{ flex: 1, background: "var(--bg-elevated)", borderRadius: "var(--radius-md)", padding: "10px 12px" }}>
                    <div style={{ fontSize: 11, color: "var(--text-faint)", marginBottom: 4 }}>Scalability</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                      <div style={{ fontSize: 20, fontWeight: 700, color: "var(--warning)" }}>{planSnap?.scalability?.score || 0}</div>
                      <div style={{ fontSize: 11, color: "var(--text-faint)" }}>/100</div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-main)" }}>{planSnap?.scalability?.label}</div>
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-faint)", marginTop: 2 }}>{planSnap?.scalability?.note}</div>
                  </div>
                </div>
              </div>
            </div>
            )}
            <div style={{ fontSize: 11, color: "var(--text-faint)", marginBottom: 16, lineHeight: 1.6 }}>
              Plan Mode simulates holding each trade to its original Stop Loss / Take Profit, independent of how the position was actually managed live.
            </div>
          </div>
          )}
          <div style={card}>
            <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 14 }}>Performance Statistics{mode==="PLAN"?" (Plan)":""}</div>
            {(() => {
              const pStats = mode === "PLAN" ? {
                totalTrades: planSnap?.totalTrades, winningTrades: planSnap?.wins, losingTrades: planSnap?.losses,
                winRate: planSnap?.winRate, profitFactor: planSnap?.profitFactor, avgWin: planSnap?.avgWin,
                avgLoss: planSnap?.avgLoss, riskReward: planSnap?.riskReward, expectancy: planSnap?.expectancy,
                roi: planSnap?.roi, maxDD: planSnap?.maxDD,
              } : stats;
              if (mode === "PLAN" && !planSnap) {
                return <div style={{ textAlign: "center", padding: 30, color: "var(--text-faint)", fontSize: 13 }}>{planAlphaData?.message || "No resolved Plan trades yet."}</div>;
              }
              return (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 10 }}>
                  {[
                    // Order: trade results → quality → account → daily risk (no Overview duplicates)
                    { label: "Total Trades",   value: pStats?.totalTrades || 0 },
                    { label: "Winning Trades", value: pStats?.winningTrades || 0,  color: "var(--success)" },
                    { label: "Losing Trades",  value: pStats?.losingTrades || 0,   color: "var(--danger)" },
                    ...(mode === "PLAN" ? [{ label: "Win Rate", value: (pStats?.winRate?.toFixed(2) || "—") + "%", color: pStats?.winRate >= 55 ? "var(--success)" : "var(--warning)" }] : []),
                    { label: "Profit Factor",  value: pStats?.profitFactor?.toFixed(2) || "—", color: pStats?.profitFactor >= 2 ? "var(--success)" : "var(--warning)" },
                    { label: "Avg Win",        value: "$" + (pStats?.avgWin?.toFixed(2) || "—"),   color: "var(--success)" },
                    { label: "Avg Loss",       value: "$" + (pStats?.avgLoss?.toFixed(2) || "—"),  color: "var(--danger)" },
                    { label: "Risk:Reward",    value: pStats?.riskReward?.toFixed(2) || "—" },
                    { label: "Expectancy",     value: "$" + (pStats?.expectancy?.toFixed(2) || "—"), color: "var(--accent)" },
                    ...(mode !== "PLAN" ? [
                      { label: "Balance", value: fMoney(snap?.balance) },
                      { label: "Deposit", value: fMoney(summary?.totalDeposit) },
                      { label: "Withdraw", value: fMoney(summary?.totalWithdraw) },
                      { label: "Daily Max DD", value: snap?.dailyMaxDdPct>=0?fPct(snap?.dailyMaxDdPct):"—", color: snap?.dailyMaxDdPct>3?"var(--danger)":snap?.dailyMaxDdPct>1?"var(--warning)":"var(--success)" },
                    ] : []),
                    ...(mode === "PLAN" ? [
                      { label: "Plan ROI", value: `${(pStats?.roi||0).toFixed(1)}%`, color: (pStats?.roi||0)>=0?"var(--success)":"var(--danger)" },
                      { label: "Max DD",   value: `${(pStats?.maxDD||0).toFixed(1)}%`,
                        color: (pStats?.maxDD||0)>=100?"#c0392b":(pStats?.maxDD||0)>40?"var(--danger)":(pStats?.maxDD||0)>20?"var(--warning)":"var(--success)" },
                    ] : []),
                  ].map(s => <StatCard key={s.label} label={s.label} value={s.value} color={s.color} />)}
                </div>
              );
            })()}
          </div>
          <WeeklyChart accountId={accountId} accountCreatedAt={selectedAccount?.created_at} deposit={summary?.totalDeposit || 0} mode={mode} />
          <MonthlyChart accountId={accountId} accountCreatedAt={selectedAccount?.created_at} deposit={summary?.totalDeposit || 0} mode={mode} />
          <EquityChart accountId={accountId} currentEquity={mode==="LIVE"?snap?.equity:0} currentBalance={mode==="LIVE"?snap?.balance:0} mode={mode} />
          <DrawdownChart accountId={accountId} mode={mode} />
          <TradingHeatmap accountId={accountId} mode={mode} />
          <PerPairTable perPairData={perPairData} />
        </div>
      )}

      {/* Tab: Live Positions */}
      {tab === "live" && <LivePositions accountId={accountId} />}
      {/* Tab: History */}
      {tab === "history" && <TradeHistory accountId={accountId} mode={mode} setMode={setMode} planData={planData} />}

      {/* Tab: Followers */}
      {tab === "followers" && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16, marginBottom: 24 }}>
            {[
              { l: "Total Followers",  v: followers?.summary?.totalFollowers ?? "—" },
              { l: "Active Followers", v: followers?.summary?.activeFollowers ?? "—" },
              { l: "Total AUM Copied", v: followers?.summary?.totalAUM ? fMoney(followers?.summary?.totalAUM) : "—" },
              { l: "Followers Closed P&L", v: followers?.summary?.totalClosedProfit != null ? fMoney(followers.summary.totalClosedProfit) : "—", color: (followers?.summary?.totalClosedProfit ?? 0) >= 0 ? "var(--success)" : "var(--danger)" },
              { l: "Est. Your Earnings", v: followers?.summary?.totalEstimatedFee != null ? fMoney(followers.summary.totalEstimatedFee) : "—", color: "var(--accent)" },
            ].map(x => (
              <div key={x.l} style={{ background: "var(--bg-surface)", border: "1px solid var(--border)",
                borderRadius: "var(--radius-lg)", padding: "16px 20px" }}>
                <div style={{ fontSize: 11, color: "var(--text-faint)", marginBottom: 6, textTransform: "uppercase",
                  letterSpacing: "0.06em" }}>{x.l}</div>
                <div style={{ fontSize: 24, fontWeight: 700, color: x.color || "var(--text-main)" }}>{x.v}</div>
              </div>
            ))}
          </div>
          {!followers ? (
            <div style={{ textAlign: "center", color: "var(--text-faint)", padding: 40 }}>Loading...</div>
          ) : followers.followers.length === 0 ? (
            <div style={{ textAlign: "center", color: "var(--text-faint)", padding: 60 }}>
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: "var(--text-main)" }}>No followers yet</div>
              <div style={{ fontSize: 13 }}>When investors copy your trading account, they will appear here.</div>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    {["Account","Provider","Status","AUM","Copies","Wins","Closed P&L","Est. Fee (Trader)","Since"].map(h => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {followers.followers.map((f, i) => (
                    <tr key={f.id} style={{ borderBottom: "1px solid var(--border-subtle)" }}>
                      <td style={{ ...td, fontWeight: 600 }}>{f.followerNickname || f.followerAccountId?.slice(0, 8)}</td>
                      <td style={{ ...td, color: "var(--text-muted)" }}>{f.providerNickname}</td>
                      <td style={td}>
                        <span style={{ padding: "2px 8px", borderRadius: "var(--radius-sm)", fontSize: 10, fontWeight: 700,
                          background: f.status === "ACTIVE" ? "var(--success-soft)" : "var(--danger-soft)",
                          color: f.status === "ACTIVE" ? "var(--success)" : "var(--danger)" }}>{f.status}</span>
                      </td>
                      <td style={{ ...td, fontWeight: 600, color: "var(--accent)" }}>{fMoney(f.followerEquity)}</td>
                      <td style={td}>{f.totalCopies}</td>
                      <td style={{ ...td, color: "var(--success)" }}>{f.wins}</td>
                      <td style={{ ...td, fontWeight: 600, color: (f.closedProfit ?? 0) >= 0 ? "var(--success)" : "var(--danger)" }}>
                        {f.closedProfit != null ? fMoney(f.closedProfit) : "—"}
                      </td>
                      <td style={{ ...td, fontWeight: 600, color: "var(--accent)" }}>
                        {f.estimatedFee != null ? fMoney(f.estimatedFee) : "—"}
                      </td>
                      <td style={{ ...td, color: "var(--text-faint)", fontSize: 11 }}>{f.createdAt?.slice(0, 10)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tab: Accounts */}
      {tab === "accounts" && (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-main)", marginBottom: 14 }}>Connected Accounts</div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr>
                {["Nickname","Broker","Platform","Account No.","Currency","Status","EA Connection","Share","Edit","Delete"].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {accounts.map(a => (
                  <tr key={a.ID || a.id}>
                    <td style={{ ...td, fontWeight: 600 }}>
                      <div>{a.nickname || "—"}</div>
                      {a.about && <div style={{ fontSize: 11, color: "var(--text-faint)", marginTop: 2 }}>
                        {a.about.slice(0, 40)}{a.about.length > 40 ? "..." : ""}
                      </div>}
                    </td>
                    <td style={td}>{a.broker || "—"}</td>
                    <td style={td}>{a.platform || "—"}</td>
                    <td style={td}>{a.account_number || a.AccountNumber || "—"}</td>
                    <td style={td}>{a.currency || "USD"}</td>
                    <td style={td}>
                      <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px",
                        borderRadius: "var(--radius-sm)",
                        background: a.status === "active" ? "var(--success-soft)" : "var(--danger-soft)",
                        color: a.status === "active" ? "var(--success)" : "var(--danger)" }}>
                        {a.status}
                      </span>
                    </td>
                    <td style={td}>
                      {(()=>{
                        const cs = a.connection_status || "pending";
                        const cfg = {
                          connected:    { bg:"var(--success-soft)", color:"var(--success)",  label:"Connected" },
                          disconnected: { bg:"var(--danger-soft)",  color:"var(--danger)",   label:"Disconnected" },
                          pending:      { bg:"var(--warning-soft)", color:"var(--warning)",  label:"Pending EA" },
                        }[cs] || { bg:"var(--warning-soft)", color:"var(--warning)", label:"Pending EA" };
                        return (
                          <div>
                            <span style={{ fontSize:11, fontWeight:600, padding:"2px 8px",
                              borderRadius:"var(--radius-sm)", background:cfg.bg, color:cfg.color }}>
                              {cfg.label}
                            </span>
                            {cs === "pending" && (
                              <div style={{ fontSize:10, color:"var(--text-faint)", marginTop:3 }}>
                                Connect EA to activate
                              </div>
                            )}
                            {cs === "disconnected" && (
                              <div style={{ fontSize:10, color:"var(--text-faint)", marginTop:3 }}>
                                EA not pushing data
                              </div>
                            )}
                            {a.ea_first_push_at && cs === "connected" && (
                              <div style={{ fontSize:10, color:"var(--text-faint)", marginTop:3 }}>
                                Verified
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td style={td}>
                      <button onClick={() => {
                          const url = `${window.location.origin}/strategy/${a.ID || a.id}`;
                          const tok = localStorage.getItem("access_token")||localStorage.getItem("token")||"";
                          fetch("/api/investor/affiliate/overview",{headers:{Authorization:`Bearer ${tok}`}})
                            .then(r=>r.json())
                            .then(d=>{
                              const ref = d?.affiliate?.code||"";
                              const shareUrl = ref?`${url}?ref=${ref}`:url;
                              navigator.clipboard.writeText(shareUrl);
                              alert("Link copied!\n\n"+shareUrl);
                            })
                            .catch(()=>{
                              navigator.clipboard.writeText(url);
                              alert("Link copied!\n\n"+url);
                            });
                        }}
                        style={{fontSize:11,padding:"4px 10px",background:"var(--accent-soft)",
                          color:"var(--accent)",border:"1px solid var(--accent-border)",
                          borderRadius:"var(--radius-sm)",cursor:"pointer",fontWeight:600,whiteSpace:"nowrap"}}>
                        Share
                      </button>
                    </td>
                    <td style={td}>
                      <button onClick={() => { setEditAccount(a); setEditNickname(a.nickname || ""); setEditAbout(a.about || ""); setEditError(""); }}
                        className="btn-ghost-sm">
                        Edit
                      </button>
                    </td>
                    <td style={td}>
                      <button onClick={() => handleDelete(a.ID || a.id)}
                        style={{ fontSize: 11, padding: "4px 10px", background: "var(--danger-soft)",
                          color: "var(--danger)", border: "1px solid rgba(239,68,68,0.3)",
                          borderRadius: "var(--radius-sm)", cursor: "pointer", fontWeight: 600 }}>
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}


      {/* EA Setup & Download — Trader */}
      {tab === "accounts" && (
        <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:"var(--radius-lg)",marginTop:12,marginBottom:12}}>
          <div onClick={()=>setEaExpanded(!eaExpanded)}
            style={{padding:"16px 24px",display:"flex",justifyContent:"space-between",alignItems:"center",cursor:"pointer"}}>
            <span style={{fontSize:13,fontWeight:700,color:"var(--text-main)"}}>EA Setup & Download</span>
            <span style={{fontSize:12,color:"var(--text-muted)",display:"inline-block",transition:"transform 0.2s",
              transform:eaExpanded?"rotate(180deg)":"rotate(0deg)"}}>▼</span>
          </div>
          {eaExpanded && (
            <div style={{padding:"0 24px 24px",borderTop:"1px solid var(--border)"}}>
              {/* Steps */}
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:16,paddingTop:16}}>
                {[
                  {n:"1",t:"Download EA",d:"Download the Trading EA for your platform"},
                  {n:"2",t:"Install in MT4/MT5",d:"Copy .ex4 or .ex5 to your terminal Experts folder"},
                  {n:"3",t:"Generate EA Key",d:"Generate your unique key below and paste into EA input"},
                  {n:"4",t:"Enable AutoTrading",d:"Turn on AutoTrading in MT4/MT5 and attach EA to any chart"},
                ].map(s=>(
                  <div key={s.n} style={{display:"flex",gap:12,alignItems:"flex-start",
                    padding:"10px 12px",background:"var(--bg-elevated)",borderRadius:"var(--radius-md)",border:"1px solid var(--border)"}}>
                    <div style={{width:22,height:22,borderRadius:"50%",background:"var(--accent)",
                      display:"flex",alignItems:"center",justifyContent:"center",
                      fontSize:11,fontWeight:700,color:"#fff",flexShrink:0}}>{s.n}</div>
                    <div>
                      <div style={{fontSize:12,fontWeight:600,color:"var(--text-main)"}}>{s.t}</div>
                      <div style={{fontSize:11,color:"var(--text-muted)",marginTop:2}}>{s.d}</div>
                    </div>
                  </div>
                ))}
              </div>
              {/* Download buttons — Publisher only */}
              <div style={{display:"flex",gap:10,marginBottom:16}}>
                <a href="/api/ea/download/mt5" download
                  style={{flex:1,padding:"10px 0",borderRadius:"var(--radius-md)",
                    background:"var(--accent-soft)",color:"var(--accent)",
                    border:"1px solid var(--accent-border)",fontSize:12,fontWeight:600,
                    textAlign:"center",textDecoration:"none",display:"block"}}>
                  Download EA Publisher MT5 (.ex5)
                </a>
                <a href="/api/ea/download/mt4" download
                  style={{flex:1,padding:"10px 0",borderRadius:"var(--radius-md)",
                    background:"rgba(167,139,250,0.1)",color:"var(--purple,#a78bfa)",
                    border:"1px solid rgba(167,139,250,0.3)",fontSize:12,fontWeight:600,
                    textAlign:"center",textDecoration:"none",display:"block"}}>
                  Download EA Publisher MT4 (.ex4)
                </a>
              </div>
              {/* EA Key Section */}
              <EAKeySection accountId={selectedAccount?.ID || selectedAccount?.id} />
            </div>
          )}
        </div>
      )}
      <AddAccountModal isOpen={showAddModal} onClose={() => setShowAddModal(false)}
        onAccountAdded={() => { setShowAddModal(false); api.getAccounts().then(d => setAccounts(d || [])); }} />

      {/* Edit Account Capital */}
      {editAccount && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
          background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center",
          justifyContent: "center", zIndex: 9999 }}>
          <div style={{ background: "var(--bg-surface)", borderRadius: "var(--radius-xl)",
            padding: 32, maxWidth: 480, width: "90%", border: "1px solid var(--border)" }}>
            <h3 style={{ marginBottom: 6, fontSize: 17, fontWeight: 700, color: "var(--text-main)" }}>Edit Account</h3>
            <p style={{ color: "var(--text-faint)", fontSize: 13, marginBottom: 24 }}>
              {editAccount.broker} · {editAccount.platform} · {editAccount.account_number}
            </p>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 500,
                color: "var(--text-muted)" }}>Account Nickname</label>
              <input type="text" value={editNickname} onChange={e => setEditNickname(e.target.value)}
                placeholder="e.g. Gold Swing, Conservative"
                style={{ width: "100%", padding: "10px 12px", borderRadius: "var(--radius-md)",
                  border: "1px solid var(--border)", background: "var(--bg)",
                  color: "var(--text-main)", fontSize: 14, boxSizing: "border-box" }} />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 500,
                color: "var(--text-muted)" }}>Account Description</label>
              <textarea value={editAbout} onChange={e => setEditAbout(e.target.value)}
                placeholder="Describe your trading strategy..."
                rows={4}
                style={{ width: "100%", padding: "10px 12px", borderRadius: "var(--radius-md)",
                  border: "1px solid var(--border)", background: "var(--bg)",
                  color: "var(--text-main)", fontSize: 14, resize: "vertical",
                  fontFamily: "inherit", boxSizing: "border-box" }} />
              <small style={{ color: "var(--text-faint)", fontSize: 12 }}>
                Shown in Marketplace to help investors understand your strategy
              </small>
            </div>
            {editError && (
              <div className="auth-error" style={{ marginBottom: 16 }}>{editError}</div>
            )}
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setEditAccount(null)} disabled={editLoading}
                className="btn btn-secondary">
                Cancel
              </button>
              <button onClick={handleEditSave} disabled={editLoading}
                className="btn btn-primary">
                {editLoading ? "Saving..." : "Save changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
